import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faFolderOpen, faPlay, faCheck, faTrash, faRedo, faEye, faRotate, faCopy
} from '@fortawesome/free-solid-svg-icons';
import DateFilterCalendar from './DateFilterCalendar';
import MessageBox from './MessageBox';
import ConfirmDialog from './ConfirmDialog';
import MCQImagePreview from './MCQImagePreview';
import { folderDisplayTitle, sameClassAndSubject } from '../utils/folderDisplay';
import { copyToClipboard } from '../utils/clipboard';
import useAiStatus from '../utils/useAiStatus';
import { LOCAL_MODELS } from '../utils/localModels';
import McqClassTable from './McqClassTable';
import McqReconciliation from './McqReconciliation';
import {
  applyMarkAbsent,
  absorbLegacyApprove,
  buildEdupagePayload,
  buildSheetReconciliation,
  formatListLabel,
  isApproveMigrated,
  loadUnmatched,
  markApproveMigrated,
  matchSheets,
  mergeServerApprove,
  persistUnmatched
} from '../utils/mcqMatch';

// ---------------------------------------------------------------------------
// MCQs > OMR sub-tab.
//
// Pipeline: reuses the Sort tab's local-Gemma pipeline so we don't need a
// separate backend route. Specifically:
//
//   1. POST /analyze/extract-roll-numbers with provider='local' + model='ggml'
//      (Gemma 4 31B ggml — same option Sort exposes) and folder_path scoped to
//      the selected scans folder. The backend kicks off a session and returns
//      202 + { session_id }.
//   2. Poll /analyze/progress/{session_id} every 2s until status==='completed'.
//   3. Fetch /analyze/results?folder_path=... once finished.
//
// Each result row from /analyze/results carries (at minimum): id, image_no,
// roll_name (e.g. "Hajrah Shahab (5T52)"), unique_code (e.g. "5T52"). If the
// backend prompt also returns per-question answers under `answers`/`mcq` or
// other metadata (class/section/variant/subject/text page), we read those too;
// otherwise the Q1–Q40 cells stay blank until the backend prompt is extended.
//
// Rows live in one working copy (localStorage + mcq_omr_results). The Check
// screen matches them against the student list in place.
// ---------------------------------------------------------------------------

export const MCQ_OMR_RESULTS_KEY = 'mcqOmrResults';

// Physical OMR sheet now has 30 bubble rows (used to be 34, previously 40).
// The "Number of MCQs" input caps how many slots are shown and copied to
// EduPage. Default is 30 — the only value the current sheet template supports.
const MAX_QUESTIONS = 30;
const MCQ_COUNT_KEY = 'mcqOmrQuestionCount';

// Expected roll-number format on the OMR sheets: ONE letter followed by TWO
// digits (e.g. "C55"). A roll that has a value but doesn't match this shape is
// highlighted red so the examiner can fix it. An empty roll is NOT a format
// error here — the existing mismatch/empty highlight already covers blanks.
const ROLL_FORMAT_RE = /^[A-Za-z][0-9]{2}$/;
const isBadRollFormat = (v) => {
  const s = (v == null ? '' : String(v)).trim();
  return s.length > 0 && !ROLL_FORMAT_RE.test(s);
};

export default function MCQsOMR() {
  const API_BASE_URL = window.API_BASE_URL;
  const [scansFolders, setScansFolders] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  // Opening MCQs shows the calendar only. Folders appear after a day, All, or Today.
  const [showAllFolders, setShowAllFolders] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  // Folder-list filter chips — mirror the AnalyzeTab top header so the two
  // tabs feel consistent. filterChecked is the only "status" axis that
  // applies to MCQ folders (they don't go through the print or sort
  // flows): 'all' | 'checked' | 'unchecked' driven by completed_at.
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterChecked, setFilterChecked] = useState('all');
  // Always default the MCQ tab to Smoffyy/qwen3.5-4b-instruct-revised on load,
  // ignoring any previously-saved pick so the default is consistent for every
  // examiner. A manual change still applies for the rest of the session.
  const [model, setModel] = useState('qwen35_4b_revised');
  // Thinking toggle — when ON we let the model use its native reasoning
  // channel (better quality but slower / risk of empty-content on token
  // truncation). When OFF the backend prepends "/no_think" so reasoning
  // models go straight to the answer. Default ON for OMR — reasoning
  // pays off on borderline bubble decisions (lightly-filled C vs. a
  // stronger neighbouring B); without it the model commits to a first
  // glance and gets row-drift errors. Users who want the speed of /no_think
  // can still untick the box and the localStorage will remember it.
  const [thinking, setThinking] = useState(() => {
    const stored = localStorage.getItem('mcqOmrThinking');
    return stored === null ? true : stored === 'true';
  });
  // Batch size — how many OMR sheets the backend processes in one AI
  // request. 1 (default) is the safest: each sheet gets a fresh prompt
  // and there's no cross-row leakage if the AI gets confused. 5 / 10 trade
  // accuracy for speed when running locally on a beefy GPU.
  const [batchSize, setBatchSize] = useState(() => {
    const raw = parseInt(localStorage.getItem('mcqOmrBatchSize') || '', 10);
    return [1, 5, 10].includes(raw) ? raw : 1;
  });
  // Number of real questions on the current paper. Dropdown values 10..40.
  // Default is null (unset) — the user must explicitly pick a count before
  // they can approve, so we don't silently assume "40" and ship wrong data
  // downstream.
  const [mcqCount, setMcqCount] = useState(() => {
    const raw = localStorage.getItem(MCQ_COUNT_KEY);
    if (raw == null || raw === '' || raw === 'null') return 30;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return 30;
    if (n < 10 || n > MAX_QUESTIONS) return 30;
    return n;
  });
  // Highest variant letter the paper supports — clamps what EduPage's
  // variant dropdown will accept. Anything beyond this is rendered with an
  // orange warning so the grader sees mis-extractions before exporting.
  // Default is null (unset) — same reasoning as mcqCount: the grader must
  // explicitly pick a value, otherwise approval is blocked downstream.
  const [maxVariant, setMaxVariant] = useState(() => {
    const raw = (localStorage.getItem('mcqOmrMaxVariant') || '').toUpperCase();
    return /^[A-J]$/.test(raw) ? raw : null;
  });
  // Row hover state — small enough that re-rendering on hover changes is
  // fine. We can't do this with pure CSS because each row carries its
  // background via inline style (for duplicate-roll red), which would
  // always defeat a :hover rule.
  const [hoveredRowId, setHoveredRowId] = useState(null);
  // Copy/paste buffers for the variant + roll-number cross-check pairs.
  // Each holds the most recently copied value (string). A paste button only
  // shows up on the OTHER cell of the same pair when the copied value is
  // non-empty and differs from the cell's current value. Cross-row paste
  // is supported because the buffer is component-scoped, not row-scoped.
  const [copiedVariant, setCopiedVariant] = useState('');
  const [copiedRoll, setCopiedRoll] = useState('');
  // Column hover state — index of the column under the cursor. Drives the
  // Excel-style column highlight via a dynamically-injected nth-child CSS
  // rule below. nth-child is the cleanest way to tint a whole column
  // without per-cell wiring: the rule targets every <th>/<td> at that
  // child position across the whole table.
  const [hoveredColIdx, setHoveredColIdx] = useState(null);
  const [processing, setProcessing] = useState(false);
  // Backend AI pipeline mutex state. `mcqBusy === true` means either
  // the auto-MCQ daemon (utils/autoMcq.daemon.js) or another teacher's
  // manual Start Checking click is currently using the local LLM. We
  // render a yellow banner + disable the kickoff button when busy.
  const aiStatus = useAiStatus(5000);
  const mcqBusy = !!(aiStatus && aiStatus.mcq && aiStatus.mcq.busy);
  const mcqBusyMsg = mcqBusy
    ? `Auto-check ${aiStatus.mcq.source === 'auto' ? 'daemon' : 'request'} is running${aiStatus.mcq.label ? ' on ' + aiStatus.mcq.label : ''}. Please wait until it finishes.`
    : '';
  const [progressText, setProgressText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [folderPath, setFolderPath] = useState('');
  // Folder-scoped saved-table snapshots — mirrors the AnalyzeTab pattern.
  // snapshotMeta is the meta for the SELECTED folder (null when none saved
  // for it); snapshotFolderStatusMap maps folder id → workflow status
  // ('saved' | 'reviewed') for every folder that has a snapshot, and
  // drives the Saved / Reviewed badges on folder cards. snapshotBusy
  // guards the Save / Review / Mark Reviewed buttons from double-clicks
  // while the round-trip is in flight.
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [snapshotFolderStatusMap, setSnapshotFolderStatusMap] = useState(() => new Map());
  // Per-rubric auto-MCQ daemon state — { attempts, last_error,
  // last_run_at, restore_status }. Mirrors AnalyzeTab's autoSortStatusMap.
  // Drives Retry auto-check (attempts >= 3) and Re-OMR (restore_status).
  const [autoMcqStatusMap, setAutoMcqStatusMap] = useState(() => new Map());
  const [startAutoMcqBusyId, setStartAutoMcqBusyId] = useState(null);
  const [rows, setRows] = useState([]);
  const [folderMeta, setFolderMeta] = useState(null); // selected folder snapshot saved with results
  // Sheets the grader sent back from a wrong match. Kept out of matching
  // until their bubbled roll is edited. Persisted per folder.
  const [userUnmatchedIds, setUserUnmatchedIds] = useState(() => new Set());
  // Examiner confirmed the current name mismatches and chose to open the
  // class table. Keyed by the mismatching row ids so a new mismatch asks again.
  const [confirmedNameKey, setConfirmedNameKey] = useState('');
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  // Login Edupage bridge + per-card Upload state (mirrors the Sort/Evaluate
  // tab). eduBridgeReady tracks whether the extension's page bridge answered;
  // uploading/uploaded sets drive the Upload button's blue→grey→green states.
  const [eduBridgeReady, setEduBridgeReady] = useState(false);
  const [uploadingFolderIds, setUploadingFolderIds] = useState(() => new Set());
  const [uploadedFolderIds, setUploadedFolderIds] = useState(() => new Set());
  // Row selection — Set of row.id for every checked row. Drives the
  // "Recheck Selected" button. Cleared after a successful recheck so the
  // grader doesn't accidentally re-run the same set twice.
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  // Anchor row index for shift-click range selection (the last checkbox the
  // user clicked without Shift). null = no anchor yet.
  const [lastCheckedIdx, setLastCheckedIdx] = useState(null);
  // Recheck session — { sessionId, rowIds:Set, progressText }. While running,
  // the main Start Checking button + per-row controls disable so we don't
  // race a recheck against a full-folder run.
  const [recheckSession, setRecheckSession] = useState(null);
  const [recheckProgressText, setRecheckProgressText] = useState('');
  // OMR import — loading flag for the "OMR" button that pulls pre-computed
  // bubble answers from omr_results.txt in the folder's local folder.
  const [omrLoading, setOmrLoading] = useState(false);
  // Pre-run image picker — when the user wants to Check only a subset of
  // the folder's scans (e.g. just 2 students for a quick test). Loaded
  // lazily from /scans/{folderId} when the picker is opened. Empty
  // pickedScanIds == "run the whole folder" (default). When the user picks
  // specific images we pass them through to the backend as scan_ids.
  const [folderScans, setFolderScans] = useState([]);
  const [folderScansLoading, setFolderScansLoading] = useState(false);
  const [pickedScanIds, setPickedScanIds] = useState(() => new Set());
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerSearch, setImagePickerSearch] = useState('');
  // Image preview state: { items: [{scan_id, file_name, label}], index }.
  // Null means closed. Used by the eye buttons in the row to let the teacher
  // verify what the student actually wrote on the sheet.
  const [preview, setPreview] = useState(null);
  // Collapse the date calendar + folder grid once the user kicks off a run,
  // so the extracted results table has more vertical space. The user can
  // re-expand via the "Change folder" button to pick a different folder.
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  // Timestamp of when the OMR extraction last finished for the loaded folder.
  // Set ONLY when the polling loop sees status === 'completed' — not on edits
  // or partial-result streams. Persisted alongside the rows so it survives
  // reloads. Drives the green "Last Checked" pill above the results table.
  const [checkedAt, setCheckedAt] = useState(null);

  const openPreview = useCallback((row, startWith = 'omr') => {
    const items = [];
    if (row.omr_scan_id) items.push({ scan_id: row.omr_scan_id, file_name: row.omr_file_name, label: 'OMR sheet' });
    if (row.text_scan_id) items.push({ scan_id: row.text_scan_id, file_name: row.text_file_name, label: 'Text page' });
    if (items.length === 0) return;
    const startIdx = startWith === 'text'
      ? Math.max(0, items.findIndex(i => i.label === 'Text page'))
      : 0;
    setPreview({ items, index: startIdx });
  }, []);

  // Persist the model choice so the user doesn't reselect each session.
  useEffect(() => { localStorage.setItem('mcqOmrModel', model); }, [model]);
  useEffect(() => { localStorage.setItem('mcqOmrThinking', String(thinking)); }, [thinking]);
  useEffect(() => { localStorage.setItem('mcqOmrBatchSize', String(batchSize)); }, [batchSize]);
  useEffect(() => {
    if (mcqCount == null) localStorage.removeItem(MCQ_COUNT_KEY);
    else localStorage.setItem(MCQ_COUNT_KEY, String(mcqCount));
  }, [mcqCount]);
  useEffect(() => {
    if (!maxVariant) localStorage.removeItem('mcqOmrMaxVariant');
    else localStorage.setItem('mcqOmrMaxVariant', maxVariant);
  }, [maxVariant]);

  // Reset the picker any time the user switches folders — the previous
  // folder's scan list and selections don't apply to the new one.
  useEffect(() => {
    setFolderScans([]);
    setPickedScanIds(new Set());
    setShowImagePicker(false);
    setImagePickerSearch('');
  }, [selectedFolderId]);

  // Fetch the folder's scan list the first time the picker is opened (or
  // when the user reopens it after switching folders). Cached in
  // `folderScans` so reopening on the same folder is instant.
  useEffect(() => {
    if (!showImagePicker) return;
    if (!selectedFolderId) return;
    if (folderScans.length > 0) return;
    let cancelled = false;
    (async () => {
      setFolderScansLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/scans/${selectedFolderId}`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const sorted = (data.scans || []).sort((a, b) => {
          return (a.file_name || '').localeCompare(b.file_name || '', undefined, { numeric: true, sensitivity: 'base' });
        });
        setFolderScans(sorted);
      } catch (err) {
        if (!cancelled) showMessage('Failed to load image list: ' + err.message, 'error');
      } finally {
        if (!cancelled) setFolderScansLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImagePicker, selectedFolderId]);

  // Only failures open a dialog. Success and status notes used to stop the
  // grader on an OK button after every check, save, and EduPage login.
  const showMessage = (text, type = 'info') => {
    if (type !== 'error') return;
    setMessage({ show: true, text, type });
  };
  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => {
    const cb = confirm.onConfirm;
    setConfirm({ show: false, text: '', onConfirm: null });
    if (cb) setTimeout(() => cb(), 50);
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  // Restore previous extraction results on mount so the user doesn't lose
  // their last batch when switching sub-tabs OR logging in on a different
  // device. We try localStorage first (instant, same-device case); when
  // that's empty we fall back to /api/mcq-omr/latest which pulls the most
  // recently saved batch for this user from the DB. That second branch is
  // what makes the tab "resume" automatically on PC2 after the user ran
  // OMR on PC1.
  useEffect(() => {
    let restoredFromLocal = false;
    try {
      const raw = localStorage.getItem(MCQ_OMR_RESULTS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.rows) && parsed.rows.length > 0) {
          setRows(parsed.rows);
          if (parsed.folder) {
            setFolderMeta(parsed.folder);
            setSelectedFolderId(String(parsed.folder.id));
          }
          if (parsed.checkedAt) setCheckedAt(parsed.checkedAt);
          restoredFromLocal = true;
        }
      }
    } catch (_) { /* ignore */ }

    if (restoredFromLocal) return;

    // Empty localStorage → ask the backend for the user's most recent
    // saved batch and populate the tab from that. Folder metadata that
    // isn't stored in mcq_omr_results (test_date, etc.) gets filled in
    // later when scansFolders loads — we keep what we know.
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/mcq-omr/latest`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        const latest = data && data.data;
        if (!latest || !Array.isArray(latest.rows) || latest.rows.length === 0) return;
        const idMatch = String(latest.folder_path || '').match(/rubrics\/(\d+)/);
        const folderId = idMatch ? idMatch[1] : null;
        setRows(latest.rows);
        if (folderId) {
          setSelectedFolderId(folderId);
          // Stub folder snapshot — gets enriched once /scans/folders loads
          // and the matching folder lands in scansFolders state.
          setFolderMeta({ id: Number(folderId) });
        }
        // Also seed localStorage so subsequent mounts are instant.
        try {
          localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify({
            rows: latest.rows,
            folder: folderId ? { id: Number(folderId) } : null,
            savedAt: Date.now()
          }));
        } catch { /* quota — ignore */ }
      } catch (err) {
        console.warn('[MCQ OMR] Latest restore failed:', err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When a folder is selected (or its id changes), pull the latest saved
  // rows from the backend. The DB is the source of truth — it survives
  // browser/device changes and is shared across users on the same folder.
  // localStorage stays as an instant-restore cache so this effect only
  // overwrites the visible table when the server returns rows.
  // Tracks which folder the visible table currently belongs to, so we can tell
  // a genuine user folder-SWITCH apart from the initial mount-restore.
  const loadedFolderRef = useRef(null);
  useEffect(() => {
    if (!selectedFolderId) return;
    // On a real folder switch (NOT the first load / mount-restore), drop the
    // previous folder's cached rows + the localStorage cache IMMEDIATELY. This
    // is the fix for the stale-list bug: without it, clicking a new folder kept
    // the old list on screen while the new one loaded — and if the new folder
    // had no saved data the load returned early, leaving the OLD list visible.
    // Clearing first means a folder with no data shows empty, and the new
    // folder's list always replaces the previous one cleanly.
    const prevFolder = loadedFolderRef.current;
    const isFolderSwitch = prevFolder != null && String(prevFolder) !== String(selectedFolderId);
    loadedFolderRef.current = selectedFolderId;
    if (isFolderSwitch) {
      setRows([]);
      setSelectedRowIds(new Set());
      try { localStorage.removeItem(MCQ_OMR_RESULTS_KEY); } catch (_) { /* ignore */ }
    }
    let cancelled = false;
    // Re-shape a DB/snapshot row into the frontend row format. Both endpoints
    // return the same fields; we just attach a stable client id so React keys
    // + selection state work. (Backend LEFT JOINs scans so file names are
    // recovered here — without them the eye-button tooltip would fall back to
    // "View scanned sheets" whenever the tab re-mounts.)
    const mapRow = (r, idx, prefix) => ({
      id: r.id || `${prefix}-${idx}`,
      omr_scan_id: r.omr_scan_id,
      text_scan_id: r.text_scan_id,
      omr_file_name: r.omr_file_name || null,
      text_file_name: r.text_file_name || null,
      student_name: r.student_name || '',
      handwritten_paper_variant: r.handwritten_paper_variant || '',
      handwritten_roll_number: r.handwritten_roll_number || '',
      class: r.class || '',
      section: r.section || '',
      subject: r.subject || '',
      roll_number: r.roll_number || '',
      paper_variant: r.paper_variant || '',
      answers: r.answers || {},
      mcq_count: r.mcq_count,
      max_variant: r.max_variant
    });
    (async () => {
      // Resolve the snapshot for the CURRENTLY selected folder up front so EVERY
      // load outcome below can refresh the localStorage cache to point at THIS
      // folder. Previously only the snapshot branch wrote the cache — the
      // working-copy and empty-folder branches called setRows but left the
      // cache (rows + folder pointer) on the PREVIOUSLY opened folder, which is
      // the bug: clicking a new folder didn't update localStorage.
      const folderObj = scansFolders.find(f => String(f.id) === String(selectedFolderId));
      const folderSnap = folderObj
        ? {
            id: folderObj.id, title: folderObj.title, class: folderObj.class,
            section: folderObj.section, subject: folderObj.subject, created_by: folderObj.created_by
          }
        : (folderMeta && String(folderMeta.id) === String(selectedFolderId) ? folderMeta : { id: Number(selectedFolderId) });
      // Write the cache for THIS folder (rows + folder pointer). Always called
      // after a successful load — including the empty case — so the cache never
      // lags behind the selected folder.
      const cacheRows = (mappedRows) => {
        if (folderSnap) setFolderMeta(folderSnap);
        try {
          localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify({
            rows: mappedRows, folder: folderSnap, savedAt: Date.now()
          }));
        } catch (_) { /* quota — ignore */ }
      };
      // Copy absentee rows and roll fixes off the old Approve tab once.
      // After that, Check is the only copy and later edits are not overwritten.
      const mergeLegacyApprove = async (mapped, fp) => {
        if (isApproveMigrated(selectedFolderId)) return mapped;
        let next = absorbLegacyApprove(mapped, selectedFolderId);
        try {
          const res = await fetch(
            `${API_BASE_URL}/mcq-omr/approve?folder_path=${encodeURIComponent(fp)}`,
            { headers: authHeaders() }
          );
          if (!res.ok) return next;
          const data = await res.json().catch(() => ({}));
          next = mergeServerApprove(next, Array.isArray(data.data) ? data.data : []);
          markApproveMigrated(selectedFolderId);
        } catch (_) { /* leave unmarked so the next open retries */ }
        return next;
      };

      try {
        const fp = `rubrics/${selectedFolderId}`;
        // Prefer the saved SNAPSHOT for this folder — that's the persisted
        // "data from the folder" the examiner expects to see the moment they
        // pick it. Falls through to the per-user working copy when the folder
        // has no snapshot yet.
        const snapRes = await fetch(
          `${API_BASE_URL}/mcq-omr/snapshots?folder_path=${encodeURIComponent(fp)}`,
          { headers: authHeaders() }
        );
        if (!cancelled && snapRes.ok) {
          const snapData = await snapRes.json().catch(() => ({}));
          const snapRows = Array.isArray(snapData.data) ? snapData.data : [];
          if (!cancelled && snapRows.length > 0) {
            let mapped = snapRows.map((r, idx) => mapRow(r, idx, 'snap'));
            mapped = await mergeLegacyApprove(mapped, fp);
            if (cancelled) return;
            setRows(mapped);
            cacheRows(mapped);
            return;
          }
        }
        if (cancelled) return;
        // No snapshot — load the per-user working copy (original behaviour).
        const res = await fetch(
          `${API_BASE_URL}/mcq-omr?folder_path=${encodeURIComponent(fp)}`,
          { headers: authHeaders() }
        );
        if (!res.ok) return;
        const data = await res.json();
        const dbRows = Array.isArray(data.data) ? data.data : [];
        if (cancelled) return;
        // Map whatever the working copy holds — including an empty list — and
        // ALWAYS refresh the cache to this folder (the previous early-return on
        // an empty working copy is what left the cache on the old folder).
        let mapped = dbRows.map((r, idx) => mapRow(r, idx, 'db'));
        mapped = await mergeLegacyApprove(mapped, fp);
        if (cancelled) return;
        setRows(mapped);
        cacheRows(mapped);
      } catch (err) {
        console.warn('[MCQ OMR] DB load failed (using local cache):', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId]);

  // Fetch all scan folders once on mount (same endpoint Sort uses).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/scans/folders`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        // MCQs-only: the OMR sub-tab is for bubble-sheet rubrics. Subjective
        // rubrics are handled in the Evaluate tab.
        const mcqOnly = (data.folders || []).filter(f => f.type === 'mcq');
        const sorted = mcqOnly.sort((a, b) => {
          const dA = new Date(a.test_date || a.created_at || 0);
          const dB = new Date(b.test_date || b.created_at || 0);
          return dB - dA;
        });
        setScansFolders(sorted);
      } catch (err) {
        if (!cancelled) showMessage('Failed to load scan folders: ' + err.message, 'error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  // Re-fetch the MCQ folder list (e.g. after an on-demand upload) so a card's
  // image count updates without a full page reload.
  const reloadScansFolders = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/scans/folders`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const mcqOnly = (data.folders || []).filter(f => f.type === 'mcq');
      mcqOnly.sort((a, b) => {
        const dA = new Date(a.test_date || a.created_at || 0);
        const dB = new Date(b.test_date || b.created_at || 0);
        return dB - dA;
      });
      setScansFolders(mcqOnly);
    } catch (_) { /* keep the old list on a refresh failure */ }
  };

  // Login Edupage bridge listener — the extension's content_bridge.js posts
  // readiness + login results back to the page. Same protocol as AnalyzeTab.
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window) return;
      const d = ev.data;
      if (!d || typeof d !== 'object' || d.source !== 'edupage-selector') return;
      if (d.type === 'EDU_BRIDGE_READY') {
        setEduBridgeReady(true);
      } else if (d.type === 'EDU_LOGIN_RESULT') {
        if (d.ok) {
          showMessage(`Opening EduPage for ${d.username || 'this account'}…`, 'success');
        } else {
          showMessage(
            d.reason === 'not_found'
              ? "EduPage login: this folder's creator email isn't in the extension's credential list."
              : 'EduPage login failed — is the Login Edupage extension installed and updated?',
            'error'
          );
        }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_BRIDGE_PING' }, '*');
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hand this folder card to the Login Edupage extension to auto-login.
  const loginToEduPageForCard = (e, folder) => {
    e.stopPropagation(); // don't also select the folder (card onClick)
    const cardEl = e.currentTarget.closest('[data-edu-card]');
    const text = cardEl ? cardEl.innerText : '';
    if (!text.trim()) return;
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_LOGIN_CARD', text }, '*');
    if (!eduBridgeReady) {
      showMessage('EduPage login needs the Login Edupage extension. Install or update it, then reload this page.', 'error');
    }
  };

  // Upload THIS folder's local images to the server on demand (same endpoint
  // the Sort/Evaluate tab uses). Only this folder is touched.
  const uploadFolderImages = async (e, folder) => {
    e.stopPropagation();
    const id = folder.id;
    if (uploadingFolderIds.has(id)) return;
    setUploadingFolderIds((prev) => new Set(prev).add(id));
    showMessage(`Uploading images for ${folderDisplayTitle(folder)}…`, 'info');
    try {
      const LOCAL_SERVER_URL = `http://${window.location.hostname}:3099`;
      const res = await fetch(`${LOCAL_SERVER_URL}/watcher/upload-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: folder.id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Upload failed (HTTP ${res.status})`);
      }
      showMessage(
        data.uploaded > 0
          ? `Uploaded ${data.uploaded} image${data.uploaded === 1 ? '' : 's'} for ${folderDisplayTitle(folder)}.`
          : `Nothing new to upload for ${folderDisplayTitle(folder)} — all ${data.total || 0} image${data.total === 1 ? '' : 's'} already on the server.`,
        'success'
      );
      setUploadedFolderIds((prev) => new Set(prev).add(id));
      reloadScansFolders();
    } catch (err) {
      showMessage(`Upload failed: ${err.message}. Is the local folder-server running on port 3099?`, 'error');
    } finally {
      setUploadingFolderIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  // Open this folder's images in the local file explorer. The browser can't
  // open a disk folder itself, so we ask the local folder-server (port 3099,
  // running where Google Drive is mounted) to open it in Windows Explorer.
  const openLocalFolder = async (e, folder) => {
    e.stopPropagation(); // don't trigger the card's folder-select click
    try {
      const LOCAL_SERVER_URL = `http://${window.location.hostname}:3099`;
      const res = await fetch(`${LOCAL_SERVER_URL}/open-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_name: folderDisplayTitle(folder), test_date: folder.test_date })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      showMessage(`Opened the local folder for ${folderDisplayTitle(folder)}.`, 'success');
    } catch (err) {
      showMessage(`Couldn't open the local folder: ${err.message}. Is the local folder-server running on port 3099?`, 'error');
    }
  };

  const dateKey = (d) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`;
  };

  // Teacher / class / subject / checked filters all intersect with the
  // selected date — same pattern AnalyzeTab uses so the chips above the
  // calendar carry through to the folder cards. When no date is selected
  // ("All"), every folder that matches the chips is returned.
  const visibleFolders = useMemo(() => {
    return scansFolders.filter(f => {
      if (filterTeacher && (f.created_by || '') !== filterTeacher) return false;
      if (filterClass && (f.class || '') !== filterClass) return false;
      if (filterSubject && (f.subject || '') !== filterSubject) return false;
      if (filterChecked !== 'all') {
        const isChecked = !!f.completed_at;
        if (filterChecked === 'checked'   && !isChecked) return false;
        if (filterChecked === 'unchecked' &&  isChecked) return false;
      }
      return true;
    });
  }, [scansFolders, filterTeacher, filterClass, filterSubject, filterChecked]);

  const foldersForSelectedDate = useMemo(() => {
    if (!selectedDate) return showAllFolders ? visibleFolders : [];
    return visibleFolders.filter(f => f.test_date && dateKey(f.test_date) === selectedDate);
  }, [visibleFolders, selectedDate, showAllFolders]);

  // Unique filter options drawn from the full folder list (so picking one
  // filter doesn't hide options in the other dropdowns).
  const teacherOptions = useMemo(() => {
    return [...new Set(scansFolders.map(f => f.created_by).filter(Boolean))].sort();
  }, [scansFolders]);
  const classOptions = useMemo(() => {
    return [...new Set(scansFolders.map(f => f.class).filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
  }, [scansFolders]);
  const subjectOptions = useMemo(() => {
    return [...new Set(scansFolders.map(f => f.subject).filter(Boolean))].sort();
  }, [scansFolders]);

  const selectedFolder = useMemo(
    () => scansFolders.find(f => String(f.id) === String(selectedFolderId)),
    [scansFolders, selectedFolderId]
  );
  // Card badge and EduPage both read the shared snapshot. 'saved' is the
  // auto-check draft (Review). EduPage stays closed until 2. Reviewed
  // has replaced that draft.
  const selectedSnapStatus = selectedFolderId
    ? (snapshotFolderStatusMap.get(String(selectedFolderId)) || null)
    : null;

  // Map one row from progressTracker.results → our row shape. The MCQ
  // controller writes records with explicit field names that match this
  // structure directly, so the mapping is mostly identity with safe defaults.
  const mapResultRow = (r, idx) => {
    // Diagnostic: log what the API row looks like for the handwritten
    // fields so we can tell whether the issue is "AI didn't emit roll_#"
    // (raw field missing) or "row came from a stale localStorage cache"
    // (no roll_# in source). Remove once verified.
    if (typeof window !== 'undefined' && window.__debugRollHash !== false) {
      try {
        console.log('[mapResultRow]', r.omr_file_name || idx,
          '| roll_#=', r['roll_#'],
          '| handwritten_roll_number=', r.handwritten_roll_number,
          '| handwritten_paper_variant=', r.handwritten_paper_variant);
      } catch (_) {}
    }
    return ({
    id: r.id != null ? String(r.id) : `${selectedFolderId}-${idx}-${r.omr_file_name || idx}`,
    omr_scan_id: r.omr_scan_id ?? null,
    text_scan_id: r.text_scan_id ?? null,
    omr_file_name: r.omr_file_name || '',
    text_file_name: r.text_file_name || '',
    // Bubbled blocks — the canonical values used for matching downstream.
    roll_number: (r.roll_number || '').toString().trim(),
    class: r.class || '',
    section: r.section || '',
    paper_variant: r.paper_variant || '',
    subject: r.subject || '',
    student_name: r.student_name || '',
    // Handwritten boxes at the top of the sheet — separate from the bubbled
    // values. Shown side-by-side in the table so the grader can spot
    // discrepancies (e.g. student wrote roll T52 in the box but bubbled T53).
    handwritten_paper_variant: r.handwritten_paper_variant || '',
    // Backend emits this field under the literal JSON key `roll_#`
    // (matches the user-facing label "Roll #"). The `#` makes that an
    // invalid JS identifier — bracket-access here, then store on the row
    // under a JS-friendly name so downstream code can use dot access.
    // Fallback to the legacy `handwritten_roll_number` key for rows that
    // were extracted before the rename.
    handwritten_roll_number: (r['roll_#'] || r.handwritten_roll_number || ''),
    answers: r.answers || {}
  });
  };

  // Persist the current rows array. Goes to BOTH localStorage (instant
  // cache so a refresh restores in the same browser without waiting on the
  // network) and the backend (durable, shared across users + devices).
  // The backend save is debounced so rapid inline edits don't hammer it.
  // flushPersistRows() cancels the debounce and awaits the POST — used by
  // Mark Reviewed so Approve's re-seed reads the corrected answers, not a
  // still-in-flight previous save.
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null); // { mapped, folderSnapshot }
  const postRowsToDb = useCallback((mapped, folderSnapshot) => {
    const fp = folderSnapshot && folderSnapshot.id ? `rubrics/${folderSnapshot.id}` : null;
    if (!fp) return Promise.resolve();
    const payload = {
      folder_path: fp,
      rows: (mapped || []).map(r => ({
        omr_scan_id: r.omr_scan_id,
        text_scan_id: r.text_scan_id,
        student_name: r.student_name,
        handwritten_paper_variant: r.handwritten_paper_variant,
        handwritten_roll_number: r.handwritten_roll_number,
        class: r.class,
        section: r.section,
        subject: r.subject,
        roll_number: r.roll_number,
        paper_variant: r.paper_variant,
        answers: r.answers || {},
        mcq_count: r.mcq_count != null ? r.mcq_count : null,
        max_variant: r.max_variant || null
      }))
    };
    return fetch(`${API_BASE_URL}/mcq-omr/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload)
    }).catch(err => console.warn('[MCQ OMR] DB save failed (cached locally):', err));
  }, [API_BASE_URL, authHeaders]);

  const persistRows = useCallback((mapped, folderSnapshot) => {
    try {
      localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify({
        rows: mapped,
        folder: folderSnapshot,
        savedAt: Date.now()
      }));
    } catch (_) { /* quota — ignore */ }

    pendingSaveRef.current = { mapped, folderSnapshot };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (pending) postRowsToDb(pending.mapped, pending.folderSnapshot);
    }, 500);
  }, [postRowsToDb]);

  const flushPersistRows = useCallback(async (mapped, folderSnapshot) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    const toSave = mapped != null
      ? { mapped, folderSnapshot }
      : pending;
    if (!toSave) return;
    try {
      localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify({
        rows: toSave.mapped,
        folder: toSave.folderSnapshot,
        savedAt: Date.now()
      }));
    } catch (_) { /* quota — ignore */ }
    await postRowsToDb(toSave.mapped, toSave.folderSnapshot);
  }, [postRowsToDb]);

  // Inline edit of a header field (Roll/Name/Class/Sec/Var/Subject). The new
  // value flows into local state AND localStorage so Approve & Match sees it.
  const updateHeaderField = useCallback((rowId, field, value) => {
    if (field === 'roll_number') {
      setUserUnmatchedIds(prev => {
        const id = String(rowId);
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        persistUnmatched(selectedFolderId, next);
        return next;
      });
    }
    setRows(prev => {
      const next = prev.map(r => r.id === rowId ? { ...r, [field]: value } : r);
      persistRows(next, folderMeta);
      return next;
    });
  }, [folderMeta, persistRows, selectedFolderId]);

  // Inline edit of an answer cell. Normalizes whatever the user typed so
  // "a" → "A", "a,b" → "A,B", "" / "·" → null. This preserves the cell-color
  // rules (blank → blue, multi → orange, single → normal).
  const updateAnswer = useCallback((rowId, q, raw) => {
    const text = String(raw || '').toUpperCase().trim();
    let normalized = null;
    if (text && text !== '·') {
      const letters = Array.from(new Set((text.match(/[ABCD]/g) || []))).sort();
      if (letters.length === 1) normalized = letters[0];
      else if (letters.length > 1) normalized = letters.join(',');
    }
    setRows(prev => {
      const next = prev.map(r => {
        if (r.id !== rowId) return r;
        return { ...r, answers: { ...r.answers, [q]: normalized } };
      });
      persistRows(next, folderMeta);
      return next;
    });
  }, [folderMeta, persistRows]);

  // Auto-forward roll cross-check mismatches to MistakesTab. A row qualifies
  // when the handwritten "Roll #" disagrees with the bubbled "Roll Number"
  // (or one side is empty while the other has content) — i.e. any state that
  // paints the pair red in the table below. Each row's id is recorded so the
  // POST runs at most once even if rows re-render or the grader later clicks
  // the green tick to resolve the mismatch (the mistake still happened —
  // logging it is the whole point of the leaderboard on MistakesTab).
  // PERMANENT: prefix matches AnalyzeTab so the cleanup cron leaves the row
  // alone.
  //
  // freshlyExtractedIdsRef is what stops mistakes from being re-posted every
  // time the grader opens a previously-checked folder. Rows that arrive via
  // DB-load or localStorage-restore are NOT marked fresh — only rows that
  // pass through storeRows / mergeRecheckResult during the current session
  // are. Mistakes for rows loaded from the DB were already logged when the
  // OMR run originally completed (and were rejected by sentMistakesRef
  // during that session); re-posting them on every folder switch / page
  // reload would duplicate the mistake every time.
  const sentRollMistakesRef = useRef(new Set());
  useEffect(() => {
    // Suppress logging mid-extraction — partial streams would otherwise
    // record spurious "missing bubbled roll" mistakes against rows the AI
    // simply hasn't reached yet.
    if (processing || recheckSession) return;
    if (!rows || rows.length === 0) return;

    const folder = folderMeta || scansFolders.find(f => String(f.id) === String(selectedFolderId));
    const folderClass = (folder && folder.class) || '';
    const folderSubject = (folder && folder.subject) || '';
    if (!folderClass && !folderSubject) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    for (const r of rows) {
      if (!r || !r.id) continue;
      // Only auto-send rows that came from a fresh extraction in this
      // session. DB-loaded / cache-restored rows are ignored — their
      // mistakes were already logged when originally extracted.
      if (!freshlyExtractedIdsRef.current.has(r.id)) continue;
      if (sentRollMistakesRef.current.has(r.id)) continue;

      const hwRoll = (r.handwritten_roll_number || '').toString().trim().toUpperCase();
      const bubRoll = (r.roll_number || '').toString().trim().toUpperCase();
      // Skip rows where BOTH sides are empty — there's no useful data to log.
      if (!hwRoll && !bubRoll) continue;
      // Clean rows (both sides present AND equal) are not mistakes.
      if (hwRoll && bubRoll && hwRoll === bubRoll) continue;

      sentRollMistakesRef.current.add(r.id);

      let detail;
      if (hwRoll && bubRoll) {
        detail = `Handwritten Roll # "${hwRoll}" disagrees with Bubbled Roll Number "${bubRoll}"`;
      } else if (hwRoll) {
        detail = `Handwritten Roll # "${hwRoll}" — Bubbled Roll Number missing`;
      } else {
        detail = `Bubbled Roll Number "${bubRoll}" — Handwritten Roll # missing`;
      }
      // Always anchor the image reference to scan_id when we have one —
      // filenames recur across folders/uploads (sequential "Image_NNN.jpg"
      // names from the scanner), so /scans/by-filename can return the
      // wrong scan. We still include the filename as a human-readable hint
      // in brackets, but extractImageRef on the MistakesTab side prefers
      // the [scan:NNN] tag for actual image lookup.
      const firstImage = r.omr_file_name || r.text_file_name || '';
      const scanId = r.omr_scan_id || r.text_scan_id || null;
      const tagParts = [];
      if (firstImage) tagParts.push(`[${firstImage}]`);
      if (scanId) tagParts.push(`[scan:${scanId}]`);
      const reason = `MCQ roll cross-check mismatch — ${detail}` + (tagParts.length ? ` · ${tagParts.join(' ')}` : '');

      fetch(`${API_BASE_URL}/students/unmatched`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          class: folderClass,
          section: r.section || '',
          subject: folderSubject,
          name: r.student_name || hwRoll || bubRoll || '(unknown)',
          marks: '',
          comments: 'PERMANENT:' + reason + (reason.includes('[mcq]') ? '' : ' [mcq]'),
          teacher_name: (folder && folder.created_by) || '',
          test_date: (folder && folder.test_date) || ''
        })
      }).catch(err => console.warn('[MCQ OMR] Mistakes log POST failed:', err));
    }
  }, [rows, folderMeta, scansFolders, selectedFolderId, processing, recheckSession, API_BASE_URL]);

  // Roll-number duplicate set — drives the red row-highlight. Computed from
  // the current rows (so edits update it immediately). Empty rolls don't
  // count as duplicates of each other.
  const duplicateRolls = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      const k = (r.roll_number || '').trim().toUpperCase();
      if (!k) continue;
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const dup = new Set();
    for (const [k, n] of counts) if (n > 1) dup.add(k);
    return dup;
  }, [rows]);

  // Sort config. `key` is either a top-level row field ('roll_number',
  // 'student_name', 'class' …) or `answer:<q>` for a question column. null
  // key means "preserve original order".
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Three-state cycle on click: unsorted → asc → desc → unsorted. Matches
  // the common spreadsheet convention so a user can "clear" the sort
  // without picking a different column.
  const requestSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;
    const { key, direction } = sortConfig;
    const getVal = (r) => {
      if (key.startsWith('answer:')) {
        const q = key.slice('answer:'.length);
        return r.answers?.[q] || '';
      }
      return r[key] == null ? '' : r[key];
    };
    // Stable copy + natural sort so "2" < "10" works, and empties go to the
    // bottom of an ascending sort (they're the least useful).
    const indexed = rows.map((r, i) => ({ r, i }));
    indexed.sort((a, b) => {
      const va = String(getVal(a.r)).trim();
      const vb = String(getVal(b.r)).trim();
      const aEmpty = va === '';
      const bEmpty = vb === '';
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return a.i - b.i;
      const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
      if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      return a.i - b.i;
    });
    return indexed.map(x => x.r);
  }, [rows, sortConfig]);

  // ── Match sheets to the folder's student list ──
  // Problem sheets stay on the flat grid. When every sheet matches and the
  // roll / variant / name cross-checks are clean, the class table replaces it.
  // Students with no sheet do not block that switch — they show as absent rows.
  const [listFilter, setListFilter] = useState('all');
  const [importedStudents, setImportedStudents] = useState([]);
  const [importedStudentsLoading, setImportedStudentsLoading] = useState(false);
  const [importedListsMeta, setImportedListsMeta] = useState([]);

  useEffect(() => {
    const ids = loadUnmatched(selectedFolderId);
    try {
      const raw = localStorage.getItem('mcqApproveState');
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && String(saved.omrFolderId) === String(selectedFolderId) && saved.unmatchedOverrides) {
          for (const id of Object.keys(saved.unmatchedOverrides)) {
            if (saved.unmatchedOverrides[id]) ids.add(String(id));
          }
          persistUnmatched(selectedFolderId, ids);
        }
      }
    } catch (_) { /* ignore a bad cache */ }
    setUserUnmatchedIds(ids);
    setListFilter('all');
  }, [selectedFolderId]);

  // Auto-import the student list(s) whose class + subject match the selected
  // folder (mirrors AnalyzeTab's reconciliation import).
  useEffect(() => {
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    const folderClass = (folder && folder.class ? folder.class : '').trim();
    const folderSubject = (folder && folder.subject ? folder.subject : '').trim();
    if (!selectedFolderId || !folderClass || !folderSubject) {
      setImportedStudents([]); setImportedListsMeta([]); return;
    }
    let cancelled = false;
    (async () => {
      setImportedStudentsLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const listsRes = await fetch(`${API_BASE_URL}/students/lists`, { headers: { Authorization: `Bearer ${token}` } });
        const allLists = listsRes.ok ? await listsRes.json() : [];
        const matchedLists = (Array.isArray(allLists) ? allLists : []).filter(l =>
          sameClassAndSubject(folder, l));
        const studentResponses = await Promise.all(matchedLists.map(l =>
          fetch(`${API_BASE_URL}/students?class=${encodeURIComponent(l.class)}&section=${encodeURIComponent(l.section || '')}&subject=${encodeURIComponent(l.subject)}`,
            { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => [])));
        const seen = new Set(); const merged = [];
        matchedLists.forEach((l, i) => {
          const listKey = `${l.class}||${l.section || ''}||${l.subject}`;
          for (const s of (studentResponses[i] || [])) {
            const key = (s.student_name || '').trim().toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            merged.push({ ...s, _listKey: listKey });
          }
        });
        merged.sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));
        if (!cancelled) { setImportedStudents(merged); setImportedListsMeta(matchedLists); }
      } catch {
        if (!cancelled) { setImportedStudents([]); setImportedListsMeta([]); }
      } finally {
        if (!cancelled) setImportedStudentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId, scansFolders]);

  const visibleStudents = useMemo(() => {
    if (listFilter === 'all') return importedStudents;
    return importedStudents.filter(s => s._listKey === listFilter);
  }, [importedStudents, listFilter]);

  const activeListLabel = useMemo(() => {
    if (listFilter !== 'all') {
      const [cls, sec, subj] = listFilter.split('||');
      return formatListLabel(cls, sec, subj);
    }
    if (importedListsMeta.length === 1) {
      const l = importedListsMeta[0];
      return formatListLabel(l.class, l.section, l.subject);
    }
    if (importedListsMeta.length > 1) return `${importedListsMeta.length} lists`;
    return '';
  }, [listFilter, importedListsMeta]);

  const sheetMatch = useMemo(
    () => matchSheets(rows, visibleStudents, userUnmatchedIds),
    [rows, visibleStudents, userUnmatchedIds]
  );
  const recon = useMemo(
    () => buildSheetReconciliation(rows, visibleStudents, userUnmatchedIds),
    [rows, visibleStudents, userUnmatchedIds]
  );
  // Rolls and duplicate sheets still have to be fixed. A different name on
  // the sheet does not block the class table, but the examiner confirms it
  // before the table opens.
  const nameMismatchKey = useMemo(() => {
    const ids = [];
    for (const bucket of recon.matched) {
      for (const r of bucket.aiRows) {
        if (r._nameMismatch) ids.push(String(r.id));
      }
    }
    ids.sort();
    return ids.join('|');
  }, [recon]);
  const rollsClear = recon.unmatchedResults.length === 0 && recon.duplicateCount === 0;
  const namesAccepted = recon.nameMismatchCount === 0 || (nameMismatchKey !== '' && confirmedNameKey === nameMismatchKey);
  const identityClear = rollsClear && namesAccepted;
  const showClassTable = !processing && !importedStudentsLoading
    && visibleStudents.length > 0 && rows.length > 0 && identityClear;

  useEffect(() => {
    setConfirmedNameKey('');
  }, [selectedFolderId]);

  // Tiny ↑/↓/⇅ indicator next to header text.
  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Tracks row ids that came from a live extraction (full-folder run or
  // per-row recheck) in the current browser session. The MistakesTab
  // auto-send effect only forwards rows whose id is in this set, so
  // re-opening a previously-checked folder doesn't re-log every existing
  // mismatch as a "new" mistake.
  const freshlyExtractedIdsRef = useRef(new Set());

  const storeRows = useCallback((rawRows, folderSnapshot) => {
    const mapped = (rawRows || []).map(mapResultRow);
    mapped.forEach(r => { if (r && r.id != null) freshlyExtractedIdsRef.current.add(r.id); });
    setRows(mapped);
    try {
      localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify({
        rows: mapped,
        folder: folderSnapshot,
        savedAt: Date.now()
      }));
    } catch (_) { /* quota — ignore */ }
    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId]);

  // OMR — import pre-computed MCQ answers from omr_results.txt in the selected
  // folder's LOCAL folder (produced by the external OMR reader) instead of
  // running the AI. Asks the loader-server (port 3101) to resolve+read the file
  // by class/section/subject/date, maps each sheet into a row, and stores them
  // exactly like a finished Start Checking (so reconciliation + Save work).
  const handleLoadOmr = useCallback(async () => {
    const folder = selectedFolder;
    if (!folder) { showMessage('Pick a folder first, then click OMR.', 'error'); return; }
    if (!folder.test_date) { showMessage('Selected folder has no test date — cannot find its local folder.', 'error'); return; }
    if (!folder.class || !folder.subject) { showMessage('Selected folder is missing class or subject.', 'error'); return; }
    setUserUnmatchedIds(new Set());
    persistUnmatched(selectedFolderId, new Set());
    setOmrLoading(true);
    try {
      const resp = await fetch(`http://${window.location.hostname}:3101/omr-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          class: folder.class,
          section: folder.section || '',
          subject: folder.subject,
          test_date: folder.test_date,
          // Use the DISPLAY title (appends " MCQs" for MCQ folders) so the local
          // server looks in "<title> MCQs …", not the raw "<title> …" — otherwise
          // it matches a same-named SUBJECTIVE folder that has no omr_results.
          folder_name: folderDisplayTitle(folder)
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        const detail = (data && data.error) ? data.error : `HTTP ${resp.status}`;
        const avail = (data && data.available && data.available.length)
          ? ` Folders in that day: ${data.available.slice(0, 8).join(', ')}${data.available.length > 8 ? ', …' : ''}` : '';
        throw new Error(`${detail}.${avail}`);
      }
      const sheets = Array.isArray(data.data) ? data.data : [];
      if (sheets.length === 0) { showMessage(`The omr_results file in "${data.folder || ''}" was empty.`, 'error'); return; }

      // Resolve each sheet's scan id from its filename so the row's image can
      // be viewed (eye button) and rechecked. Canonical key matches
      // "Image_02293.jpg" to a backend scan named exactly that OR a prefixed
      // "1778_Image_02293.jpg". Best-effort: if the scan list can't be fetched,
      // rows still import (just without an image link).
      const canonName = (n) => {
        const x = String(n || '').toLowerCase();
        const m = x.match(/image[_-]?(\d+)/);
        return m ? `image_${m[1]}` : x.replace(/\.[a-z0-9]+$/, '');
      };
      const scanIdByName = {};
      try {
        const sres = await fetch(`${API_BASE_URL}/scans/${selectedFolderId}`, { headers: authHeaders() });
        if (sres.ok) {
          const sdata = await sres.json();
          for (const sc of (sdata.scans || [])) {
            if (sc && sc.file_name != null && sc.id != null) scanIdByName[canonName(sc.file_name)] = sc.id;
          }
        }
      } catch (_) { /* import without image links */ }

      // Map each OMR sheet → the raw row shape storeRows/mapResultRow expects.
      const rawRows = sheets.map((s) => {
        const hv = s.header_verification || {};
        const hvVal = (k) => (hv[k] && hv[k].value != null ? String(hv[k].value).trim() : '');
        const srcAns = s.answers || {};
        const answers = {};
        for (const q of Object.keys(srcAns)) {
          const v = (srcAns[q] && srcAns[q].value != null ? String(srcAns[q].value) : '').toUpperCase().trim();
          answers[q] = v || null; // blank → null so the cell paints blue
        }
        // Bubbled roll number: the real OMR splits it into letter + two
        // digits (B+2+2 = "B22"); older exports use a single roll_number.
        const letter = hvVal('roll_number_letter');
        const d1 = hvVal('roll_number_1st_digit');
        const d2 = hvVal('roll_number_2nd_digit');
        const rollCombined = (letter || d1 || d2) ? `${letter}${d1}${d2}` : '';
        const bubRoll = rollCombined || hvVal('roll_number');
        const bubVariant = hvVal('paper_variant') || hvVal('variant');
        const fileName = s.filename || s.image_name || '';
        return {
          omr_file_name: fileName,
          omr_scan_id: scanIdByName[canonName(fileName)] || null,
          roll_number: bubRoll,
          class: hvVal('class'),
          section: hvVal('section'),
          paper_variant: bubVariant,
          subject: hvVal('subject'),
          student_name: hvVal('student_name'),
          // OMR has no reliable handwritten "Roll #" / "Paper Variant" boxes,
          // so mirror the BUBBLED values into the handwritten columns. This
          // makes the roll/variant cross-check always pass for OMR rows, so
          // they're never flagged red (per request: OMR imports shouldn't be
          // marked red on roll/variant mismatch). Manual checks are unaffected.
          handwritten_roll_number: bubRoll,
          handwritten_paper_variant: bubVariant,
          answers
        };
      });
      const snap = folderMeta || { id: folder.id, class: folder.class, section: folder.section, subject: folder.subject, title: folder.title, test_date: folder.test_date };
      const mapped = storeRows(rawRows, snap);
      persistRows(mapped, snap);
      showMessage(`Imported ${mapped.length} sheet(s) from omr_results (${data.folder || ''}).`, 'success');
    } catch (err) {
      const msg = String(err && err.message ? err.message : err);
      const hint = /Failed to fetch|NetworkError/i.test(msg) ? ' — is the loader-server running on port 3101? (npm run loader-server)' : '';
      showMessage('OMR import failed: ' + msg + hint, 'error');
    } finally {
      setOmrLoading(false);
    }
  }, [selectedFolder, selectedFolderId, folderMeta, storeRows, persistRows, authHeaders, API_BASE_URL]);

  // Delete a row by id — used by the trash icon in the # cell when the
  // grader recognises a wrong/duplicate scan. Persists the trimmed list so
  // Approve & Match doesn't see it on next tab switch.
  const dropRow = useCallback((rowId) => {
    showConfirm('Delete this row? The OMR sheet stays in S3 — only this row is removed from FastGrader.', () => {
      setRows(prev => {
        const next = prev.filter(r => r.id !== rowId);
        try {
          const raw = localStorage.getItem(MCQ_OMR_RESULTS_KEY);
          const parsed = raw ? JSON.parse(raw) : {};
          parsed.rows = next;
          parsed.savedAt = Date.now();
          localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify(parsed));
        } catch (_) { /* ignore */ }
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper for variant validation. Returns one of:
  //   'ok'       — variant is within the configured max
  //   'empty'    — no variant at all (purple)
  //   'oor'      — variant is past the configured max (orange)
  const variantStatus = useCallback((v) => {
    const s = (v || '').toString().trim().toUpperCase();
    if (!s) return 'empty';
    const ch = s[0];
    if (!/[A-Z]/.test(ch)) return 'oor';
    // Until the grader picks a max-variant cap we can't say the variant is
    // out-of-range — treat any letter as ok in that state.
    if (!maxVariant) return 'ok';
    return ch > maxVariant ? 'oor' : 'ok';
  }, [maxVariant]);

  // Poll /analyze/progress while a session is running.
  useEffect(() => {
    if (!sessionId || !processing) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/analyze/progress/${sessionId}`, { headers: authHeaders() });
        if (!res.ok) {
          if (res.status === 404) {
            setProcessing(false);
            setSessionId(null);
            setProgressText('');
            showMessage('OMR session expired or server restarted. Please run again.', 'error');
          }
          return;
        }
        const data = await res.json();
        const prog = data.progress || {};
        if (cancelled) return;

        // The MCQ controller writes each parsed pair to progressTracker.results,
        // and the existing /analyze/progress endpoint surfaces `results` in the
        // payload on completion (and as a partial stream while processing).
        if (Array.isArray(prog.results) && prog.results.length > 0) {
          storeRows(prog.results, folderMeta);
        }

        if (prog.status === 'completed') {
          setProgressText(`Completed ${prog.completed || 0} sheet(s).`);
          setProcessing(false);
          setSessionId(null);
          const mapped = storeRows(prog.results || [], folderMeta);
          const now = Date.now();
          setCheckedAt(now);
          try {
            const raw = localStorage.getItem(MCQ_OMR_RESULTS_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            parsed.checkedAt = now;
            localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify(parsed));
          } catch (_) { /* quota — ignore */ }
          showMessage(`OMR complete — ${mapped.length} sheet(s) processed.`, 'success');
        } else if (prog.status === 'failed') {
          setProcessing(false);
          setSessionId(null);
          setProgressText('');
          showMessage(prog.error || 'OMR failed.', 'error');
        } else if (prog.status === 'cancelled') {
          setProcessing(false);
          setSessionId(null);
          setProgressText('Cancelled.');
          storeRows(prog.results || [], folderMeta);
          showMessage('OMR cancelled — partial results loaded.', 'info');
        } else {
          // processing / queued / etc
          const label = prog.currentStudent
            ? `Processing ${prog.currentStudent} (${prog.completed || 0}/${prog.total || 0})…`
            : `Processing… ${prog.completed || 0}/${prog.total || 0}`;
          setProgressText(label);
        }
      } catch (err) {
        // network blip — silent, next tick will retry
      }
    };

    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, processing, API_BASE_URL, folderPath]);

  // Merge a fresh result row coming back from the AI into the existing rows
  // table by matching on omr_scan_id. Preserves the original row's React `id`
  // so the row's DOM node doesn't churn (keeps focus / hover / sort
  // position). Used by the per-row recheck flow.
  const mergeRecheckResult = useCallback((newResult) => {
    if (!newResult || newResult.omr_scan_id == null) return;
    setRows(prev => {
      const next = prev.map(r => {
        if (r.omr_scan_id == null) return r;
        if (String(r.omr_scan_id) !== String(newResult.omr_scan_id)) return r;
        // Build the mapped row using the existing mapping but stash the
        // original id so React doesn't tear down the row.
        const mapped = mapResultRow(newResult, 0);
        // Mark as freshly extracted so the MistakesTab auto-send effect
        // re-evaluates this row — a recheck can introduce a new roll
        // mismatch that wasn't there before.
        if (r.id != null) freshlyExtractedIdsRef.current.add(r.id);
        return { ...mapped, id: r.id };
      });
      persistRows(next, folderMeta);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderMeta, persistRows]);

  // Kick off a recheck for every currently-checked row that has an
  // omr_scan_id. Synthetic rows (no scan id) are skipped — there's no image
  // for the AI to re-read.
  const recheckSelected = useCallback(async () => {
    if (recheckSession || processing) return;
    if (!model) {
      showMessage('Pick a local model first.', 'error');
      return;
    }
    const targets = rows.filter(r => selectedRowIds.has(r.id) && r.omr_scan_id != null);
    if (targets.length === 0) {
      showMessage('Select one or more rows with a scanned sheet to recheck.', 'info');
      return;
    }
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId))
      || folderMeta
      || null;
    const fp = folder?.id ? `rubrics/${folder.id}` : folderPath;
    if (!fp) {
      showMessage('Folder context is missing — pick a folder first.', 'error');
      return;
    }
    const scanIds = targets.map(t => t.omr_scan_id);
    const rowIds = new Set(targets.map(t => t.id));
    setRecheckProgressText(`Rechecking ${targets.length} sheet(s)…`);
    try {
      const res = await fetch(`${API_BASE_URL}/analyze/extract-mcq-answers`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folder_path: fp,
          batch_size: Math.min(batchSize, scanIds.length),
          provider: 'local',
          local_model: model,
          disable_thinking: !thinking,
          scan_ids: scanIds
        })
      });
      if (res.status === 202) {
        const data = await res.json().catch(() => ({}));
        if (data.session_id) {
          setRecheckSession({ sessionId: data.session_id, rowIds });
          return;
        }
        throw new Error('Backend returned 202 without a session_id');
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Recheck failed (HTTP ${res.status})`);
    } catch (err) {
      setRecheckProgressText('');
      showMessage('Recheck failed: ' + err.message, 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, model, rows, selectedRowIds, recheckSession, processing,
      scansFolders, selectedFolderId, folderMeta, folderPath, thinking, authHeaders]);

  // Poll the recheck session and merge each completed sheet's result back
  // into the corresponding row by scan_id. Streams partial results while
  // running so a multi-sheet recheck updates rows progressively.
  useEffect(() => {
    if (!recheckSession?.sessionId) return;
    let cancelled = false;
    const seen = new Set();
    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/analyze/progress/${recheckSession.sessionId}`, { headers: authHeaders() });
        if (!res.ok) {
          if (res.status === 404) {
            setRecheckSession(null);
            setRecheckProgressText('');
            showMessage('Recheck session expired.', 'error');
          }
          return;
        }
        const data = await res.json();
        const prog = data.progress || {};
        if (cancelled) return;

        if (Array.isArray(prog.results)) {
          for (const r of prog.results) {
            const k = String(r.omr_scan_id);
            if (seen.has(k)) continue;
            seen.add(k);
            mergeRecheckResult(r);
          }
        }

        if (prog.status === 'completed') {
          setRecheckProgressText(`Recheck completed (${prog.completed || 0}/${prog.total || 0}).`);
          setRecheckSession(null);
          // Clear the selection now that the rows are refreshed.
          setSelectedRowIds(new Set());
          showMessage(`Recheck complete — ${prog.completed || 0} sheet(s) refreshed.`, 'success');
          // Clear the progress text after a beat.
          setTimeout(() => setRecheckProgressText(''), 1500);
        } else if (prog.status === 'failed') {
          setRecheckSession(null);
          setRecheckProgressText('');
          showMessage(prog.error || 'Recheck failed.', 'error');
        } else if (prog.status === 'cancelled') {
          setRecheckSession(null);
          setRecheckProgressText('Cancelled.');
          showMessage('Recheck cancelled — partial results merged.', 'info');
        } else {
          const label = prog.currentStudent
            ? `Rechecking ${prog.currentStudent} (${prog.completed || 0}/${prog.total || 0})…`
            : `Rechecking… ${prog.completed || 0}/${prog.total || 0}`;
          setRecheckProgressText(label);
        }
      } catch (_) { /* silent — next tick retries */ }
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckSession?.sessionId, API_BASE_URL]);

  const runOMR = async () => {
    if (!selectedFolderId) {
      showMessage('Please select a scans folder first.', 'error');
      return;
    }
    if (!model) {
      showMessage('Please pick a local model first.', 'error');
      return;
    }
    // `selectedFolderId` may have been restored from localStorage before
    // `/scans/folders` finished loading, or the folder may have been
    // deleted server-side since the last save. In either case `folder`
    // is undefined and dereferencing `folder.id` crashes runOMR. Fall
    // back to the existing folderMeta / selectedFolderId so the request
    // still goes through with the right rubric id.
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId)) || null;
    const rubricId = folder?.id ?? folderMeta?.id ?? selectedFolderId;
    if (!rubricId) {
      showMessage('Could not resolve the scans folder. Please re-pick it from the calendar above.', 'error');
      return;
    }
    const fp = `rubrics/${rubricId}`;
    const meta = folder ? {
      id: folder.id,
      title: folder.title,
      // Stash type so downstream consumers (Approve & Match, the saved label
      // in localStorage) can render the same " MCQs" suffix.
      type: folder.type,
      test_date: folder.test_date,
      class: folder.class,
      section: folder.section,
      subject: folder.subject,
      created_by: folder.created_by
    } : (folderMeta || null);  // preserve any meta loaded from cache

    setFolderPath(fp);
    setFolderMeta(meta);
    setUserUnmatchedIds(new Set());
    persistUnmatched(selectedFolderId, new Set());
    setProcessing(true);
    setProgressText(pickedScanIds.size > 0
      ? `Starting (${pickedScanIds.size} picked image${pickedScanIds.size === 1 ? '' : 's'})…`
      : 'Starting…');
    try {
      // If the user picked specific images via the Pick Images panel, send
      // them as scan_ids so the backend only processes those — otherwise
      // (empty Set) the whole folder runs as before.
      const picked = pickedScanIds.size > 0 ? Array.from(pickedScanIds) : undefined;
      // New OMR-specific endpoint with the MCQ system prompt baked in on the backend.
      const res = await fetch(`${API_BASE_URL}/analyze/extract-mcq-answers`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folder_path: fp,
          batch_size: Math.min(batchSize, picked?.length || batchSize),
          provider: 'local',
          local_model: model,
          disable_thinking: !thinking,
          ...(picked ? { scan_ids: picked } : {})
        })
      });

      if (res.status === 202) {
        const data = await res.json().catch(() => ({}));
        if (data.session_id) {
          setSessionId(data.session_id);
          setProgressText('OMR started — running locally on ' + model + '…');
          return;
        }
        throw new Error('Backend returned 202 without a session_id');
      }

      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `OMR failed (HTTP ${res.status})`);
    } catch (err) {
      setProcessing(false);
      setSessionId(null);
      setProgressText('');
      showMessage('OMR failed: ' + err.message, 'error');
    }
  };

  const stopOMR = async () => {
    if (!sessionId) {
      setProcessing(false);
      setProgressText('');
      return;
    }
    setProgressText('Stopping…');
    try {
      await fetch(`${API_BASE_URL}/analyze/cancel/${sessionId}`, {
        method: 'POST',
        headers: authHeaders()
      });
    } catch (_) { /* ignore */ }
    // Don't tear down processing/sessionId here — let the polling tick see the
    // cancelled status on its next pass (within ~2s) and run its cancelled
    // branch, which loads the partial results into the table. Setting them
    // false now would unmount the poll effect and we'd lose the partial rows.
  };

  // Delete only the rows the user has ticked. Drops them from local state,
  // persists the remaining set to the backend (replaceForFolder), and clears
  // the row-selection. Confirm dialog mirrors clearResults so the user can't
  // wipe rows by accident. No-op when nothing is selected.
  const deleteSelectedRows = () => {
    const ids = new Set(selectedRowIds);
    const count = ids.size;
    if (count === 0) return;
    showConfirm(`Delete ${count} selected row(s)? This cannot be undone.`, () => {
      setRows(prev => {
        const next = prev.filter(r => !ids.has(r.id));
        persistRows(next, folderMeta);
        return next;
      });
      setSelectedRowIds(new Set());
      showMessage(`Deleted ${count} row(s).`, 'info');
    });
  };

  const clearResults = () => {
    showConfirm('Delete all OMR results? This cannot be undone.', () => {
      // Wipe DB rows for the currently-loaded folder too — if we only
      // cleared local state the next folder-load would re-pull them and
      // the user would think clearing didn't work.
      const fp = folderMeta && folderMeta.id ? `rubrics/${folderMeta.id}` : null;
      setRows([]);
      setFolderMeta(null);
      setCalendarCollapsed(false);
      setCheckedAt(null);
      localStorage.removeItem(MCQ_OMR_RESULTS_KEY);
      if (fp) {
        fetch(`${API_BASE_URL}/mcq-omr?folder_path=${encodeURIComponent(fp)}`, {
          method: 'DELETE',
          headers: authHeaders()
        }).catch(err => console.warn('[MCQ OMR] DB delete failed:', err));
      }
      showMessage('Results cleared.', 'info');
    });
  };

  // ─── Folder-scoped saved-table snapshots ───────────────────────────────
  // Mirrors AnalyzedPaperSnapshot wiring in AnalyzeTab. Save writes the
  // current rows to mcq_omr_snapshots (folder-scoped, shared across
  // users); Import replaces THIS user's live mcq_omr_results for the
  // folder with the snapshot rows.
  const fetchSnapshotMeta = useCallback(async (path) => {
    if (!path) { setSnapshotMeta(null); return null; }
    try {
      const res = await fetch(
        `${API_BASE_URL}/mcq-omr/snapshots/meta?folder_path=${encodeURIComponent(path)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) { setSnapshotMeta(null); return null; }
      const data = await res.json().catch(() => ({}));
      const meta = data && data.meta ? data.meta : null;
      setSnapshotMeta(meta);
      return meta;
    } catch (err) {
      console.warn('[MCQ OMR] fetchSnapshotMeta failed:', err);
      setSnapshotMeta(null);
      return null;
    }
  }, [API_BASE_URL, authHeaders]);

  const fetchSnapshotFolderSet = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/mcq-omr/snapshots/folders`, {
        headers: authHeaders()
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data.data) ? data.data : [];
      // Map of folderId → status so card badges can render the correct
      // workflow state. Old clients on older snapshots without the column
      // get 'saved' as the safe default.
      const next = new Map();
      for (const r of list) {
        const m = String(r.folder_path || '').match(/rubrics\/(\d+)/);
        if (m) next.set(m[1], r.status || 'saved');
      }
      setSnapshotFolderStatusMap(next);
    } catch (err) {
      console.warn('[MCQ OMR] fetchSnapshotFolderSet failed:', err);
    }
    // Piggy-back the auto-MCQ status fetch onto the same effect that
    // primes the snapshot-status map — both feed folder-card UI and
    // both should refresh on the same 30s cadence.
    try {
      const statusRes = await fetch(`${API_BASE_URL}/mcq-omr/auto-status`, {
        headers: authHeaders()
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json().catch(() => ({}));
        const map = new Map();
        for (const row of (statusData.data || [])) {
          map.set(String(row.id), {
            attempts: row.attempts || 0,
            last_error: row.last_error || null,
            last_run_at: row.last_run_at || null,
            locked_until: row.locked_until || null,
            requested_at: row.requested_at || null,
            restore_status: row.restore_status || null
          });
        }
        setAutoMcqStatusMap(map);
      }
    } catch (err) {
      console.warn('[MCQ OMR] auto-status fetch failed:', err);
    }
  }, [API_BASE_URL, authHeaders]);

  // Reset auto-MCQ attempts on a rubric so the daemon picks it up again
  // next tick. Used by the "Retry auto-check" button shown when the
  // attempt counter hits 3.
  const handleRetryAutoMcq = useCallback(async (folderId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/mcq-omr/auto-retry/${folderId}`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showMessage(data.message || 'Auto-check retry queued', 'success');
        setAutoMcqStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            ...(prev.get(String(folderId)) || {}),
            attempts: 0,
            last_error: null,
            requested_at: prev.get(String(folderId))?.requested_at || new Date().toISOString()
          });
          return next;
        });
      } else {
        showMessage(data.message || 'Failed to reset auto-check', 'error');
      }
    } catch (err) {
      showMessage('Failed to reset auto-check: ' + err.message, 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, authHeaders]);

  const handleStartAutoMcq = useCallback(async (folderId, folderTitle) => {
    if (startAutoMcqBusyId) return;
    setStartAutoMcqBusyId(String(folderId));
    try {
      const res = await fetch(`${API_BASE_URL}/mcq-omr/auto-start/${folderId}`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showMessage(data.message || `Auto-MCQ started for ${folderTitle || 'this folder'}`, 'success');
        setAutoMcqStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            ...(prev.get(String(folderId)) || {}),
            attempts: 0,
            last_error: null,
            requested_at: new Date().toISOString()
          });
          return next;
        });
      } else {
        showMessage(data.message || 'Failed to start auto-MCQ', 'error');
      }
    } catch (err) {
      showMessage('Failed to start auto-MCQ: ' + err.message, 'error');
    } finally {
      setStartAutoMcqBusyId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, authHeaders, startAutoMcqBusyId]);

  // Queue a Review / Reviewed folder for another auto-MCQ pass. The daemon
  // clears the old snapshot when it starts, re-runs OMR, then restores the
  // prior Review / Reviewed status on the new snapshot.
  const handleReOmr = useCallback(async (folderId, folderTitle, opts = {}) => {
    const snapSt = snapshotFolderStatusMap.get(String(folderId)) || 'saved';
    try {
      const res = await fetch(`${API_BASE_URL}/mcq-omr/auto-reomr/${folderId}`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showMessage(data.message || 'Re-OMR queued', 'success');
        setAutoMcqStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            ...(prev.get(String(folderId)) || {}),
            attempts: 0,
            last_error: null,
            restore_status: data.restore_status || snapSt,
            requested_at: prev.get(String(folderId))?.requested_at || new Date().toISOString()
          });
          return next;
        });
        setScansFolders(prev => prev.map(f =>
          String(f.id) === String(folderId) ? { ...f, completed_at: null } : f
        ));
      } else {
        showMessage(data.message || 'Failed to queue re-OMR', 'error');
      }
    } catch (err) {
      showMessage('Failed to queue re-OMR: ' + err.message, 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, authHeaders, snapshotFolderStatusMap]);

  // Probe the selected folder for an existing snapshot on first paint and
  // whenever the user switches folders. Drives the Save button label
  // (Save vs. Update) and gates the Import button.
  useEffect(() => {
    const fp = selectedFolderId ? `rubrics/${selectedFolderId}` : '';
    fetchSnapshotMeta(fp);
  }, [selectedFolderId, fetchSnapshotMeta]);

  // One-shot probe for the badge-set on mount. Cheap — single SELECT.
  useEffect(() => {
    fetchSnapshotFolderSet();
    // Re-poll every 30s so snapshots saved by the background auto-MCQ
    // daemon (utils/autoMcq.daemon.js) flip the "💾 Saved" badge on
    // without a manual refresh, and Retry-auto-check buttons appear
    // when the daemon hits its 3-attempt ceiling.
    const refreshHandle = setInterval(() => { fetchSnapshotFolderSet(); }, 30_000);
    const onFocus = () => fetchSnapshotFolderSet();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(refreshHandle);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchSnapshotFolderSet]);

  // Shared snapshot writer. `status` drives the three-examiner workflow:
  //   • 'saved'    — examiner 1's draft. Default for the Save button.
  //   • 'reviewed' — examiner 2's confirmed copy. Set by Mark Reviewed.
  // Confirm-dialog wording is tailored to the action so the teacher knows
  // what they're about to do (overwriting a draft vs. marking it final).
  const writeSnapshot = async (status, opts = {}) => {
    const fp = selectedFolderId ? `rubrics/${selectedFolderId}` : folderPath;
    if (!fp) {
      showMessage('Select a folder first', 'error');
      return;
    }
    const sourceRows = opts.rowsOverride || rows;
    if (!sourceRows || sourceRows.length === 0) {
      showMessage('No rows to save — run Start first', 'error');
      return;
    }
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    const folderTitle = folder ? folderDisplayTitle(folder) : fp;

    setSnapshotBusy(true);
    try {
      await flushPersistRows(sourceRows, folderMeta || (selectedFolderId
        ? { id: Number(selectedFolderId) || selectedFolderId }
        : null));

      const payloadRows = sourceRows.map(r => ({
        omr_scan_id: r.omr_scan_id,
        text_scan_id: r.text_scan_id,
        student_name: r.student_name,
        handwritten_paper_variant: r.handwritten_paper_variant,
        handwritten_roll_number: r.handwritten_roll_number,
        class: r.class,
        section: r.section,
        subject: r.subject,
        roll_number: r.roll_number,
        paper_variant: r.paper_variant,
        answers: r.answers || {},
        mcq_count: r.mcq_count != null ? r.mcq_count : null,
        max_variant: r.max_variant || null
      }));
      const res = await fetch(`${API_BASE_URL}/mcq-omr/snapshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ folder_path: fp, rows: payloadRows, status })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        let successMsg = status === 'reviewed'
          ? `Reviewed — saved ${payloadRows.length} row(s) and updated "${folderTitle}" to status "reviewed"`
          : (data.message || `Saved ${payloadRows.length} row(s) to "${folderTitle}"`);
        if (status === 'reviewed') {
          if (opts.markedAbsent) successMsg += ' Absentees filled.';
          if (opts.needCount) successMsg += ' Set the number of MCQs to fill absentees.';
          if (opts.problemCount > 0) {
            successMsg += ` ${opts.problemCount} sheet(s) still need a fix before the class table opens.`;
          }
          showMessage(successMsg, opts.problemCount > 0 || opts.needCount ? 'info' : 'success');
        } else if (!opts.silent) {
          showMessage(successMsg, 'success');
        }
        await fetchSnapshotMeta(fp);
        await fetchSnapshotFolderSet();
      } else {
        showMessage(data.message || 'Failed to save snapshot', 'error');
      }
    } catch (err) {
      showMessage('Failed to save snapshot: ' + err.message, 'error');
    } finally {
      setSnapshotBusy(false);
    }
  };
  const handleMarkReviewed = () => {
    if (showClassTable && mcqCount != null) {
      const result = applyMarkAbsent(rows, visibleStudents, sheetMatch, mcqCount);
      setRows(result.rows);
      const snap = folderMeta || (selectedFolderId ? { id: Number(selectedFolderId) || selectedFolderId } : null);
      persistRows(result.rows, snap);
      writeSnapshot('reviewed', { silent: true, rowsOverride: result.rows, markedAbsent: true });
      return;
    }
    writeSnapshot('reviewed', {
      silent: true,
      problemCount: showClassTable ? 0 : (recon.unmatchedResults.length + recon.nameMismatchCount + recon.duplicateCount),
      needCount: showClassTable && mcqCount == null
    });
  };

  const unmatchSheet = useCallback((rowId) => {
    setUserUnmatchedIds(prev => {
      const next = new Set(prev);
      next.add(String(rowId));
      persistUnmatched(selectedFolderId, next);
      return next;
    });
  }, [selectedFolderId]);

  const markFolderCompleted = useCallback(async () => {
    if (!selectedFolderId) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/scans/folders/${selectedFolderId}/mark-completed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showMessage(data.message || 'Could not mark this folder Edupaged.', 'error');
        return false;
      }
      setScansFolders(prev => prev.map(f => (
        String(f.id) === String(selectedFolderId) ? { ...f, completed_at: new Date().toISOString() } : f
      )));
      return true;
    } catch (err) {
      showMessage('Could not mark this folder Edupaged: ' + err.message, 'error');
      return false;
    }
  }, [API_BASE_URL, selectedFolderId, authHeaders]);

  const handleCopyToEdupage = useCallback(async () => {
    if (selectedSnapStatus !== 'reviewed') {
      showMessage('Mark this folder Reviewed before copying to EduPage. That saves the checked table onto the folder.', 'error');
      return;
    }
    if (mcqCount == null || !maxVariant) {
      showMessage('Set MCQs and Max var before copying to EduPage.', 'error');
      return;
    }
    const payload = buildEdupagePayload({
      students: visibleStudents,
      match: sheetMatch,
      mcqCount,
      folderTitle: folderMeta ? folderDisplayTitle(folderMeta) : '',
      listLabel: activeListLabel
    });
    const json = JSON.stringify(payload, null, 2);
    try { window.lastEdupagePayload = json; } catch (_) {}
    const marked = await markFolderCompleted();
    if (!marked) return;
    const clipboardOk = await copyToClipboard(json);
    const matchedCount = payload.students.filter(s => s.variant).length;
    const bridgeResult = await new Promise((resolve) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', onResp);
        resolve({ noExtension: true });
      }, 1500);
      const onResp = (e) => {
        if (!e || !e.data || e.data.fastgraderResponse !== true) return;
        clearTimeout(timeout);
        window.removeEventListener('message', onResp);
        resolve({
          ok: e.data.ok,
          error: e.data.error,
          contextInvalidated: !!e.data.contextInvalidated
        });
      };
      window.addEventListener('message', onResp);
      window.postMessage({
        fastgraderTarget: 'edupage-extension',
        action: 'run-all',
        payload,
        leadInMs: 6000,
        delay: 80,
        mcqOffset: 1
      }, '*');
    });
    if (bridgeResult.ok) {
      showMessage(
        `Sent to EduPage extension — switching tabs and typing in 6s. ${payload.students.length} student(s), ${matchedCount} with variants.`,
        'success'
      );
    } else if (bridgeResult.noExtension) {
      showMessage(
        clipboardOk
          ? 'Extension didn\'t respond. Hard-reload this page (Ctrl+F5), or paste from the clipboard: open EduPage, click the extension icon, Reload from clipboard.'
          : 'Could not copy to clipboard and no extension responded. Hard-reload this page (Ctrl+F5), then try again.',
        'error'
      );
    } else if (bridgeResult.contextInvalidated) {
      showMessage(
        'The extension was reloaded while this page was open. Hard-reload this page (Ctrl+F5) and click Copy to EduPage again. The payload is on the clipboard.',
        'error'
      );
    } else {
      const isOpenedEduPage = /Opened EduPage/i.test(bridgeResult.error || '');
      if (!isOpenedEduPage) {
        showMessage(
          (bridgeResult.error || 'EduPage extension reported an error')
          + ' The clipboard copy is still there as a backup.',
          'error'
        );
      }
    }
  }, [mcqCount, maxVariant, visibleStudents, sheetMatch, folderMeta, activeListLabel, markFolderCompleted, selectedSnapStatus]);

  const handleImportSnapshot = async (pathArg, opts = {}) => {
    // Accept an explicit folder path — the per-card Review button passes one
    // so it doesn't have to wait for setSelectedFolderId to flush. When used
    // as a click handler, pathArg is the event object, so we fall back to
    // the selected folder.
    const fp = (typeof pathArg === 'string' && pathArg)
      ? pathArg
      : (selectedFolderId ? `rubrics/${selectedFolderId}` : folderPath);
    if (!fp) {
      showMessage('Select a folder first', 'error');
      return;
    }
    setSnapshotBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/mcq-omr/snapshots/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ folder_path: fp })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showMessage(data.message || 'Failed to import snapshot', 'error');
        return;
      }
      const dbRows = Array.isArray(data.data) ? data.data : [];
      const mapped = dbRows.map((r, idx) => ({
        id: r.id || `snap-${idx}`,
        omr_scan_id: r.omr_scan_id,
        text_scan_id: r.text_scan_id,
        omr_file_name: r.omr_file_name || null,
        text_file_name: r.text_file_name || null,
        student_name: r.student_name || '',
        handwritten_paper_variant: r.handwritten_paper_variant || '',
        handwritten_roll_number: r.handwritten_roll_number || '',
        class: r.class || '',
        section: r.section || '',
        subject: r.subject || '',
        roll_number: r.roll_number || '',
        paper_variant: r.paper_variant || '',
        answers: r.answers || {},
        mcq_count: r.mcq_count,
        max_variant: r.max_variant
      }));
      setRows(mapped);
      const folderId = String(fp).match(/rubrics\/(\d+)/)?.[1] || selectedFolderId;
      try {
        const folder = scansFolders.find(f => String(f.id) === String(folderId));
        const folderSnap = folder ? {
          id: folder.id, title: folder.title, class: folder.class,
          section: folder.section, subject: folder.subject,
          created_by: folder.created_by
        } : (folderMeta || null);
        if (folderSnap) setFolderMeta(folderSnap);
        localStorage.setItem(MCQ_OMR_RESULTS_KEY, JSON.stringify({
          rows: mapped, folder: folderSnap, savedAt: Date.now()
        }));
      } catch (_) { /* quota — ignore */ }
      setCalendarCollapsed(true);
      showMessage(data.message || `Imported ${mapped.length} row(s) from saved snapshot`, 'success');
    } catch (err) {
      showMessage('Failed to import snapshot: ' + err.message, 'error');
    } finally {
      setSnapshotBusy(false);
    }
  };

  // Per-card Review — selects the folder and loads its saved MCQ table,
  // same as Sort tab's per-card Review. Only shown when a snapshot exists.
  const reviewFolderFromCard = (e, folder) => {
    e.stopPropagation();
    const path = `rubrics/${folder.id}`;
    setSelectedFolderId(String(folder.id));
    handleImportSnapshot(path, { silent: true });
  };

  const formatFolderLabel = (folder) => {
    const dateStr = folder.test_date
      ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : '';
    return `${folderDisplayTitle(folder) || 'Folder'}${dateStr ? ' — ' + dateStr : ''} (${folder.scan_count || 0} images)`;
  };

  // ───────────────────────────────────────────────── render
  return (
    <div style={{ padding: '8px 10px', width: '100%', ...(calendarCollapsed ? { height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' } : null) }}>
      {/* AI-pipeline mutex banner — surfaces the backend's aiMutex MCQ
          slot state. Yellow tint, shown while the auto-MCQ daemon or
          another teacher's manual Start Checking is using the local
          LLM. Stays visible until the slot is released. */}
      {mcqBusy && (
        <div
          role="status"
          style={{
            marginBottom: '10px',
            padding: '10px 14px',
            background: 'linear-gradient(135deg, rgba(250,204,21,0.15), rgba(245,158,11,0.12))',
            border: '1px solid #facc15',
            borderRadius: 8,
            color: '#92400e',
            fontSize: 13,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <i className="fa-solid fa-triangle-exclamation" style={{ color: '#d97706', fontSize: 16 }} />
          <span style={{ flex: 1 }}>
            {aiStatus.mcq.source === 'auto' ? 'Auto-MCQs daemon is running' : 'Another MCQ check is in progress'}
            {aiStatus.mcq.label ? ` on ${aiStatus.mcq.label}` : ''}. Start is disabled until it finishes.
          </span>
        </div>
      )}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{
          margin: 0,
          fontSize: '1.1rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1,
          minWidth: 0
        }}>
          <FontAwesomeIcon icon={faFolderOpen} style={{ color: '#3B82F6' }} />
          MCQs — Check
          <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
            · Pick a scans folder, send the sheets to AI, and review the extracted MCQ answers.
          </span>
        </h2>

        {/* Teacher / Class / Subject chips — mirror the AnalyzeTab top
            header so the two tabs feel consistent. Same icon palette,
            same colours, same sizes. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fa-solid fa-user-tie" style={{ color: '#8B5CF6', fontSize: '0.9375rem' }}></i>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Teacher:</label>
          <select
            value={filterTeacher}
            onChange={(e) => setFilterTeacher(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
              minWidth: 150,
              background: 'var(--surface)',
              color: 'var(--text-primary)'
            }}
          >
            <option value="">All Teachers</option>
            {teacherOptions.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fa-solid fa-graduation-cap" style={{ color: '#3B82F6', fontSize: '0.9375rem' }}></i>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Class:</label>
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
              minWidth: 120,
              background: 'var(--surface)',
              color: 'var(--text-primary)'
            }}
          >
            <option value="">All Classes</option>
            {classOptions.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fa-solid fa-book" style={{ color: '#10b981', fontSize: '0.9375rem' }}></i>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Subject:</label>
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
              minWidth: 150,
              background: 'var(--surface)',
              color: 'var(--text-primary)'
            }}
          >
            <option value="">All Subjects</option>
            {subjectOptions.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* All / Today shortcut — mirrors the same toggle on ScansTab.
            "All" clears the date selection so every folder is reachable
            via the calendar; "Today" jumps to today's YYYY-MM-DD key. */}
        {(() => {
          const d = new Date();
          const todayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const isAll = showAllFolders && !selectedDate;
          const isToday = selectedDate === todayKey;
          return (
            <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', background: 'var(--tab-bg, rgba(0,0,0,0.04))', padding: 3, borderRadius: 8 }}>
              <button
                onClick={() => { setSelectedDate(''); setShowAllFolders(true); setSelectedFolderId(''); }}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: isAll ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                  color: isAll ? 'white' : 'var(--text-primary)',
                  transition: 'all 0.2s'
                }}
              >
                All
              </button>
              <button
                onClick={() => { setSelectedDate(todayKey); setShowAllFolders(false); setSelectedFolderId(''); }}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: isToday ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                  color: isToday ? 'white' : 'var(--text-primary)',
                  transition: 'all 0.2s'
                }}
              >
                Today
              </button>
            </div>
          );
        })()}

        {/* Hide/Show folders toggle — pinned to the extreme RIGHT of the
            header row so the action is always in the same spot. Reuses
            calendarCollapsed which is also flipped by Start Checking +
            Change folder; this button just lets the user do it manually. */}
        <button
          onClick={() => setCalendarCollapsed(c => !c)}
          title={calendarCollapsed ? 'Show the calendar + folder picker' : 'Hide the calendar + folder picker to give the table more room'}
          style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            border: '1px solid var(--border-color, #d1d5db)',
            borderRadius: 6,
            background: 'var(--panel-bg, white)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: '0.78rem',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap'
          }}
        >
          <i className={`fa-solid fa-${calendarCollapsed ? 'eye' : 'eye-slash'}`}></i>
          {calendarCollapsed ? 'Show folders' : 'Hide folders'}
        </button>
      </div>

      {/* Calendar + folder grid — collapsed once Start Checking is clicked
          so the extracted-results table has more vertical room. The header
          row's "Show folders" toggle brings it back; the selected-folder
          name is already shown in the Pick Images summary row below, so
          no separate summary bar is rendered here. clearResults() also
          re-expands. */}
      {!calendarCollapsed && (
        <>
      {/* Two-column layout: compact 3-col calendar on the LEFT in a narrow
          rail, folder list for the selected date on the RIGHT. Mirrors
          ScansTab / SavedRubrics so the three tabs feel identical. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ width: 360, flexShrink: 0 }}>
          <DateFilterCalendar
            items={scansFolders}
            dateField="test_date"
            selectedDate={selectedDate}
            onDateChange={(d) => { setSelectedDate(d); setShowAllFolders(false); setSelectedFolderId(''); }}
            itemLabelSingular="Folder"
            itemLabelPlural="Folders"
            title="Select Date"
            columns={5}
          />

          {/* Checked / Unchecked segmented filter — pinned to the left
              rail so it stays anchored under the calendar regardless of
              how tall the folder grid on the right grows. Matches the
              equivalent segmented control on AnalyzeTab. (MCQ folders
              don't have a Sorted state, so only the Status axis is
              relevant here.) */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', marginTop: 8 }}>
            <div style={{
              display: 'inline-flex',
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--surface)',
              flex: '1 1 0',
              minWidth: 0
            }}>
              {['all', 'checked', 'unchecked'].map(v => (
                <button
                  key={v}
                  onClick={() => setFilterChecked(v)}
                  style={{
                    flex: 1,
                    padding: '7px 4px',
                    border: 'none',
                    background: filterChecked === v ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'transparent',
                    color: filterChecked === v ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

      {(selectedDate || showAllFolders) && (
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Folder list header — shows count + "All dates" marker when no
            date is selected (matches the AnalyzeTab pattern so clicking
            "All" shows every visible folder instead of an empty grid). */}
        <div style={{
          fontSize: '0.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--text-secondary)',
          marginBottom: '6px'
        }}>
          <FontAwesomeIcon icon={faFolderOpen} style={{ color: '#f59e0b', marginRight: '6px' }} />
          {foldersForSelectedDate.length > 0
            ? `${foldersForSelectedDate.length} folder${foldersForSelectedDate.length !== 1 ? 's' : ''}`
            : 'No folders'}
          {!selectedDate && (
            <span style={{ marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted, #94a3b8)' }}>
              · All dates
            </span>
          )}
        </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '10px'
          }}>
            {foldersForSelectedDate.map(folder => {
              // Status priority + colors mirror AnalyzeTab so a folder reads
              // the same way wherever it appears: selected → amber, empty →
              // red, checked → green, default → neutral. MCQ folders don't
              // go through the print flow, so no Printed state — the
              // printed_at tint is intentionally skipped here.
              const isSel = String(folder.id) === String(selectedFolderId);
              const isEmpty = (folder.scan_count || 0) === 0;
              const isDone = !!folder.completed_at;
              const borderColor = isSel
                ? '#F59E0B'
                : isEmpty
                  ? '#EF4444'
                  : isDone
                    ? '#28a745'
                    : null;
              const bgColor = isSel
                ? 'rgba(245, 158, 11, 0.14)'
                : isEmpty
                  ? 'rgba(239, 68, 68, 0.08)'
                  : isDone
                    ? 'rgba(40, 167, 69, 0.08)'
                    : 'var(--card-bg, white)';
              const cardBorder = borderColor
                ? `3px solid ${borderColor}`
                : '1px solid var(--border-color, #e0e0e0)';
              return (
                <div
                  key={folder.id}
                  data-edu-card="1"
                  onClick={() => !isEmpty && setSelectedFolderId(String(folder.id))}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '14px',
                    borderRadius: '12px',
                    border: cardBorder,
                    background: bgColor,
                    cursor: isEmpty ? 'not-allowed' : 'pointer',
                    opacity: isEmpty ? 0.7 : 1,
                    boxShadow: isSel ? '0 4px 12px rgba(245,158,11,0.25)' : '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => { if (!isSel && !isEmpty) { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)'; } }}
                  onMouseLeave={(e) => { if (!isSel && !isEmpty) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; } }}
                >
                  {/* Status pills (Checked / Empty) — moved out of the
                      absolute-positioned top-right slot and into the title
                      flex row below, so they no longer overlap long
                      folder names. */}
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: '0.92rem',
                      color: isSel ? '#B45309' : 'var(--text-primary)',
                      marginBottom: '6px',
                      wordBreak: 'break-word',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexWrap: 'wrap'
                    }}>
                      <span>{folderDisplayTitle(folder)}</span>
                    </div>
                    {folder.assignment_name && (
                      <div
                        title={`Assignment: ${folder.assignment_name}`}
                        style={{ fontSize: '0.76rem', color: '#1D4ED8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px', minWidth: 0 }}
                      >
                        <i className="fa-solid fa-tag" style={{ width: '12px', color: '#2563EB' }}></i>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {folder.assignment_name}
                        </span>
                      </div>
                    )}
                    {folder.test_date && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                        <i className="fa-solid fa-calendar-day" style={{ width: '12px', color: '#6b7280' }}></i>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                    <div
                      onClick={(e) => openLocalFolder(e, folder)}
                      title="Open this folder's images in the local file explorer"
                      style={{ fontSize: '0.76rem', color: isEmpty ? '#DC2626' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: isEmpty ? 700 : 400, cursor: 'pointer', width: 'fit-content' }}>
                      <i className="fa-solid fa-file-image" style={{ width: '12px', color: isEmpty ? '#DC2626' : '#6b7280' }}></i>
                      {folder.scan_count || 0} image{(folder.scan_count || 0) !== 1 ? 's' : ''}
                    </div>
                    {folder.created_by && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '3px' }}>
                        <i className="fa-solid fa-user" style={{ width: '12px', color: '#6b7280' }}></i>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.created_by}</span>
                      </div>
                    )}
                    {/* Per-card actions: EduPage Login (always green) + Upload
                        (blue → grey → green "Uploaded"). Same behaviour as the
                        Sort/Evaluate tab; Upload pushes only THIS folder. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {folder.created_by && (
                        <button
                          onClick={(e) => loginToEduPageForCard(e, folder)}
                          title={eduBridgeReady
                            ? `Log into EduPage as ${folder.created_by} and open this test`
                            : `Log into EduPage as ${folder.created_by} — needs the Login Edupage extension installed & updated`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700,
                            border: '1px solid #059669', borderRadius: 6,
                            background: 'transparent', color: '#059669', cursor: 'pointer', whiteSpace: 'nowrap'
                          }}
                        >
                          <i className="fa-solid fa-right-to-bracket"></i>
                          Login
                        </button>
                      )}
                      {(() => {
                        const isUploading = uploadingFolderIds.has(folder.id);
                        const isUploaded = uploadedFolderIds.has(folder.id);
                        // blue = ready, grey = in progress, green = done.
                        // Uploaded renders as a transparent (outline) button.
                        const color = isUploading ? '#6b7280' : isUploaded ? '#059669' : '#2563eb';
                        const bg = 'transparent';
                        const fg = color;
                        const label = isUploading ? 'Uploading…' : isUploaded ? 'Uploaded' : 'Upload';
                        const icon = isUploading ? 'fa-solid fa-spinner fa-spin'
                          : isUploaded ? 'fa-solid fa-circle-check'
                          : 'fa-solid fa-image';
                        return (
                          <button
                            onClick={(e) => uploadFolderImages(e, folder)}
                            disabled={isUploading}
                            title={isUploaded
                              ? `Uploaded — click to upload again if you added more images (${folder.scan_count || 0} on the server)`
                              : `Upload this folder's images to the server now (${folder.scan_count || 0} currently on the server)`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700,
                              border: `1px solid ${color}`, borderRadius: 6,
                              background: bg, color: fg,
                              cursor: isUploading ? 'wait' : 'pointer', whiteSpace: 'nowrap'
                            }}
                          >
                            <i className={icon}></i>
                            {label}
                          </button>
                        );
                      })()}
                      {snapshotFolderStatusMap.has(String(folder.id)) && (
                        <button
                          onClick={(e) => reviewFolderFromCard(e, folder)}
                          disabled={snapshotBusy}
                          title="Load this folder's saved MCQ table"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700,
                            border: '1px solid #6366f1', borderRadius: 6,
                            background: 'transparent', color: '#6366f1',
                            cursor: snapshotBusy ? 'wait' : 'pointer', whiteSpace: 'nowrap'
                          }}
                        >
                          <i className={snapshotBusy ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-magnifying-glass'}></i>
                          {snapshotBusy ? 'Loading…' : 'Load'}
                        </button>
                      )}
                    </div>
                    {/* Status badges — placed LAST in the card's DOM order so a
                        manual copy/selection reads top-to-bottom in visual
                        order: title → assignment → date → images → email →
                        Login → Upload → badges (Checked / Reviewed) last. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {(() => {
                        const hasSnap = snapshotFolderStatusMap.has(String(folder.id));
                        const autoSt = autoMcqStatusMap.get(String(folder.id)) || {};
                        const requested = !!(autoSt.requested_at || autoSt.restore_status);
                        const mcqFp = String(aiStatus?.mcq?.folderPath || '');
                        const checkingThis = !!(aiStatus?.mcq?.busy && (
                          mcqFp === `rubrics/${folder.id}` || mcqFp.endsWith(`rubrics/${folder.id}`)
                        ));
                        const failed = !hasSnap && (autoSt.attempts || 0) >= 3;
                        const startBusy = startAutoMcqBusyId === String(folder.id);
                        const queued = !hasSnap && requested && !checkingThis && !startBusy && !failed;
                        const inProgress = !hasSnap && (checkingThis || startBusy) && !failed;
                        const pill = {
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          padding: '2px 7px',
                          borderRadius: 10,
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        };
                        // After auto-MCQ finishes, Start is replaced by the
                        // existing Review / Reviewed badges — do not label it
                        // "Checked".
                        if (hasSnap) return null;
                        if (isEmpty) return null;
                        if (inProgress) {
                          return (
                            <span
                              title="Auto-MCQ is running on this folder"
                              style={{
                                ...pill,
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                color: 'white',
                                boxShadow: '0 1px 3px rgba(37,99,235,0.35)',
                                opacity: 0.9
                              }}
                            >
                              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                              Checking…
                            </span>
                          );
                        }
                        if (queued) {
                          return (
                            <span
                              title="Start was pressed — waiting for the auto-MCQ daemon to pick this folder up"
                              style={{
                                ...pill,
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                color: 'white',
                                boxShadow: '0 1px 3px rgba(37,99,235,0.35)',
                                opacity: 0.9
                              }}
                            >
                              Queued
                            </span>
                          );
                        }
                        if (failed) return null;
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!startBusy) handleStartAutoMcq(folder.id, folderDisplayTitle(folder));
                            }}
                            disabled={startBusy}
                            title="Start auto-MCQ for this folder when the scans are ready."
                            style={{
                              ...pill,
                              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                              color: 'white',
                              boxShadow: '0 1px 3px rgba(37,99,235,0.4)',
                              cursor: startBusy ? 'wait' : 'pointer'
                            }}
                          >
                            <i className="fa-solid fa-play" aria-hidden="true" />
                            Start
                          </button>
                        );
                      })()}
                      {isDone && (
                        <span
                          title="This folder has been Edupaged — marks copied into EduPage (completed_at is stamped)"
                          style={{
                            background: 'linear-gradient(135deg, #28a745, #1e7e34)',
                            color: 'white',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            padding: '2px 7px',
                            borderRadius: 10,
                            textTransform: 'uppercase',
                            boxShadow: '0 1px 3px rgba(40,167,69,0.4)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          ✓ Edupaged
                        </span>
                      )}
                      {!isDone && isEmpty && (
                        <span
                          title="No images uploaded yet"
                          style={{
                            background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                            color: 'white',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            padding: '2px 7px',
                            borderRadius: 10,
                            textTransform: 'uppercase',
                            boxShadow: '0 1px 3px rgba(239,68,68,0.4)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Empty
                        </span>
                      )}
                      {snapshotFolderStatusMap.has(String(folder.id)) && (() => {
                        const snapStatus = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                        const isReviewed = snapStatus === 'reviewed';
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              if (!snapshotBusy) reviewFolderFromCard(e, folder);
                            }}
                            disabled={snapshotBusy}
                            title={isReviewed
                              ? 'Reviewed — click to load this folder’s MCQ table'
                              : 'Saved — click to load this folder’s MCQ table'}
                            style={{
                              background: isReviewed
                                ? 'linear-gradient(135deg, #16a34a, #15803d)'
                                : 'linear-gradient(135deg, #0EA5E9, #0284C7)',
                              color: 'white',
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              padding: '2px 7px',
                              borderRadius: 10,
                              textTransform: 'uppercase',
                              boxShadow: isReviewed
                                ? '0 1px 3px rgba(22,163,74,0.4)'
                                : '0 1px 3px rgba(14,165,233,0.4)',
                              whiteSpace: 'nowrap',
                              border: 'none',
                              cursor: snapshotBusy ? 'wait' : 'pointer'
                            }}
                          >
                            {isReviewed ? '✓ Reviewed' : '💾 Review'}
                          </button>
                        );
                      })()}
                      {/* Auto-MCQ failure → Retry button. Shown only when
                          the daemon has hit the 3-attempt ceiling AND
                          this folder doesn't already have a snapshot (no
                          point retrying something already done). Click
                          resets attempts so the daemon re-picks the
                          folder on its next 5-minute tick. */}
                      {!snapshotFolderStatusMap.has(String(folder.id))
                        && (autoMcqStatusMap.get(String(folder.id))?.attempts || 0) >= 3 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRetryAutoMcq(folder.id); }}
                          title={`Auto-check failed ${autoMcqStatusMap.get(String(folder.id))?.attempts || 0}× — last error: ${autoMcqStatusMap.get(String(folder.id))?.last_error || 'unknown'}. Click to reset and let the background daemon try again.`}
                          style={{
                            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                            color: 'white',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            padding: '2px 7px',
                            borderRadius: 10,
                            textTransform: 'uppercase',
                            boxShadow: '0 1px 3px rgba(239,68,68,0.4)',
                            whiteSpace: 'nowrap',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          ↻ Retry auto-check
                        </button>
                      )}
                      {/* Re-OMR — for folders already in Review / Reviewed.
                          Queues another auto-MCQ pass; daemon restores the
                          same badge after. Also shown as "Re-OMR…" while a
                          queued re-OMR is in flight (snapshot may already
                          be cleared). */}
                      {(() => {
                        const snapSt = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                        const pending = !!(autoMcqStatusMap.get(String(folder.id))?.restore_status);
                        const canReOmr = snapshotFolderStatusMap.has(String(folder.id)) && (snapSt === 'saved' || snapSt === 'reviewed');
                        if (!canReOmr && !pending) return null;
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!pending) {
                                handleReOmr(folder.id, folderDisplayTitle(folder), { edupaged: isDone });
                              }
                            }}
                            disabled={pending}
                            title={pending
                              ? 'Re-OMR is queued — the background daemon will re-check this folder and restore its Review status shortly.'
                              : 'Queue this folder for auto-MCQ again. The current saved table is replaced; Review / Reviewed status is restored when checking finishes. Edupaged is cleared so you can paste again.'}
                            style={{
                              background: pending
                                ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                                : 'linear-gradient(135deg, #f59e0b, #d97706)',
                              color: 'white',
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              padding: '2px 7px',
                              borderRadius: 10,
                              textTransform: 'uppercase',
                              boxShadow: pending
                                ? '0 1px 3px rgba(100,116,139,0.35)'
                                : '0 1px 3px rgba(245,158,11,0.4)',
                              whiteSpace: 'nowrap',
                              border: 'none',
                              cursor: pending ? 'default' : 'pointer',
                              opacity: pending ? 0.85 : 1
                            }}
                          >
                            {pending ? '↻ Re-OMR…' : '↻ Re-OMR'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
            {foldersForSelectedDate.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                No folders match the current filters.
              </div>
            )}
          </div>
        </div>
        )}
      </div>{/* /flex row: calendar + folder list */}
        </>
      )}

      {/* Pick Images — expanded picker panel only. The trigger button +
          helper text now live inside the action bar below; this block
          renders the per-image checkbox grid only when the picker is
          toggled open. */}
      {selectedFolderId && showImagePicker && (
        <div style={{
          background: 'var(--card-bg, white)',
          border: '1px solid var(--border-color, #e0e0e0)',
          borderRadius: '8px',
          padding: '6px 10px',
          marginBottom: '8px'
        }}>
            <div style={{ marginTop: 0, paddingTop: 0 }}>
              {folderScansLoading ? (
                <div style={{ padding: '14px', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading images…
                </div>
              ) : folderScans.length === 0 ? (
                <div style={{ padding: '14px', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  No images in this folder.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="Search by file name…"
                      value={imagePickerSearch}
                      onChange={(e) => setImagePickerSearch(e.target.value)}
                      style={{
                        flex: 1,
                        minWidth: 200,
                        padding: '6px 10px',
                        border: '1px solid var(--border-color, #d1d5db)',
                        borderRadius: 6,
                        fontSize: '0.82rem',
                        background: 'var(--panel-bg, white)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    <button
                      onClick={() => {
                        // "Select all visible" — respects the current search
                        // filter so the user can search → select that subset.
                        const q = imagePickerSearch.trim().toLowerCase();
                        const visible = folderScans.filter(s => !q || (s.file_name || '').toLowerCase().includes(q));
                        setPickedScanIds(prev => {
                          const next = new Set(prev);
                          const allChecked = visible.length > 0 && visible.every(s => next.has(s.id));
                          if (allChecked) visible.forEach(s => next.delete(s.id));
                          else visible.forEach(s => next.add(s.id));
                          return next;
                        });
                      }}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: 6,
                        background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Toggle Visible
                    </button>
                    {pickedScanIds.size > 0 && (
                      <button
                        onClick={() => setPickedScanIds(new Set())}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: 6,
                          background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Clear ({pickedScanIds.size})
                      </button>
                    )}
                  </div>
                  <div style={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    padding: '4px 2px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 6
                  }}>
                    {(() => {
                      const q = imagePickerSearch.trim().toLowerCase();
                      const visible = folderScans.filter(s => !q || (s.file_name || '').toLowerCase().includes(q));
                      if (visible.length === 0) {
                        return (
                          <div style={{ gridColumn: '1 / -1', padding: '12px', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            No images match "{imagePickerSearch}".
                          </div>
                        );
                      }
                      return visible.map(s => {
                        const checked = pickedScanIds.has(s.id);
                        return (
                          <label
                            key={s.id}
                            title={s.file_name}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 8px',
                              border: checked ? '2px solid #3B82F6' : '1px solid var(--border-color, #e5e7eb)',
                              borderRadius: 6,
                              background: checked ? 'rgba(59,130,246,0.10)' : 'var(--panel-bg, white)',
                              cursor: 'pointer',
                              fontSize: '0.78rem',
                              minWidth: 0
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setPickedScanIds(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(s.id);
                                  else next.delete(s.id);
                                  return next;
                                });
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              minWidth: 0
                            }}>
                              {s.file_name}
                            </span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                </>
              )}
            </div>
        </div>
      )}

      {/* Action bar */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: '8px',
        padding: '6px 10px',
        background: 'var(--card-bg, white)',
        border: '1px solid var(--border-color, #e0e0e0)',
        borderRadius: '8px'
      }}>
        {/* Sheets-extracted pill pinned to the extreme LEFT of the
            action bar per user request. Sits before the "Selected:"
            text so the count is the first thing the eye lands on. */}
        {rows.length > 0 && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(16,185,129,0.10)',
            border: '1px solid rgba(16,185,129,0.35)',
            color: '#065f46',
            fontSize: '0.78rem',
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}>
            <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', fontSize: '0.72rem' }} />
            {rows.length}
          </span>
        )}

        {/* Delete — placed between the sheet-count pill and the folder name
            so it sits at the far left of the action bar, per user request. */}
        {rows.length > 0 && !processing && (
          <button
            onClick={selectedRowIds.size > 0 ? deleteSelectedRows : clearResults}
            title={selectedRowIds.size > 0
              ? `Delete the ${selectedRowIds.size} ticked row(s) and keep the rest.`
              : 'Delete all extracted OMR rows for this folder.'}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #EF4444, #DC2626)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}

        <div style={{ flex: 1, minWidth: 180, fontSize: '0.82rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            {selectedFolder
              ? <strong>{folderDisplayTitle(selectedFolder) || 'Folder'}</strong>
              : <span style={{ color: 'var(--text-secondary)' }}>No folder selected.</span>}
            {processing && progressText && (
              <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#2563eb' }}>
                <FontAwesomeIcon icon={faSpinner} spin /> {progressText}
              </div>
            )}
            {(recheckSession || recheckProgressText) && (
              <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#7c3aed' }}>
                <FontAwesomeIcon icon={faRotate} spin={!!recheckSession} /> {recheckProgressText || 'Rechecking…'}
              </div>
            )}
          </div>
          {(selectedFolder?.created_by || folderMeta?.created_by) && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              <i className="fa-solid fa-user" style={{ marginRight: 4 }} />
              {selectedFolder?.created_by || folderMeta?.created_by}
            </span>
          )}
        </div>

        {/* Pick Images trigger — moved INTO the action bar so it shares a
            single row with the Model/Batch/etc. controls. The expanded
            picker grid still drops below as its own panel (gated by
            showImagePicker). Only visible once a folder is selected. */}
        {selectedFolderId && (
          <button
            onClick={() => setShowImagePicker(s => !s)}
            title="Pick which images to check. Useful for testing on 2-3 students before running the whole folder."
            style={{
              padding: '6px 10px',
              border: 'none',
              borderRadius: '6px',
              background: pickedScanIds.size > 0
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #6B7280, #4B5563)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.78rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fa-solid fa-user-check"></i>
            Pick Images
            {pickedScanIds.size > 0 && (
              <span style={{
                marginLeft: 2,
                padding: '1px 6px',
                background: 'rgba(255,255,255,0.25)',
                borderRadius: 10,
                fontSize: '0.7rem',
                fontWeight: 700
              }}>{pickedScanIds.size}</span>
            )}
            <i className={`fa-solid fa-chevron-${showImagePicker ? 'up' : 'down'}`} style={{ fontSize: '0.65rem', marginLeft: 2 }}></i>
          </button>
        )}

        <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Model:
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={processing}
            title={LOCAL_MODELS.find(m => m.value === model)?.label || model}
            style={{
              padding: '6px 8px',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: 6,
              background: 'var(--panel-bg, white)',
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              maxWidth: 180,
              textOverflow: 'ellipsis'
            }}
          >
            {LOCAL_MODELS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>

        <label
          title="How many OMR sheets to send to the AI in one request. 1 = safest (each sheet gets its own prompt — no cross-row contamination). 5 / 10 = faster when running locally on a strong GPU, at the cost of occasional batch-confusion errors."
          style={{
            fontSize: '0.85rem',
            fontWeight: 700,
            color: '#1e40af',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            border: '2px solid #3B82F6',
            background: 'rgba(59,130,246,0.08)',
            borderRadius: 6
          }}
        >
          Batch:
          <select
            value={String(batchSize)}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if ([1, 5, 10].includes(n)) setBatchSize(n);
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: 4,
              background: 'var(--panel-bg, white)',
              color: 'var(--text-primary)',
              fontSize: '0.85rem',
              fontWeight: 700,
              minWidth: 60
            }}
          >
            <option value="1">1</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </select>
        </label>

        <label
          title="How many real questions are on this paper. AI still reads all 40 bubble rows, but only the first N are shown and copied to EduPage."
          style={{
            fontSize: '0.85rem',
            fontWeight: 700,
            color: mcqCount == null ? '#b45309' : '#1e40af',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            border: mcqCount == null ? '2px solid #f59e0b' : '2px solid #3B82F6',
            background: mcqCount == null ? 'rgba(245,158,11,0.10)' : 'rgba(59,130,246,0.08)',
            borderRadius: 6
          }}
        >
          MCQs:
          <select
            value={mcqCount == null ? '' : String(mcqCount)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') setMcqCount(null);
              else {
                const n = parseInt(v, 10);
                if (Number.isFinite(n)) setMcqCount(Math.max(10, Math.min(MAX_QUESTIONS, n)));
              }
            }}
            style={{
              padding: '4px 4px',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: 4,
              background: 'var(--panel-bg, white)',
              color: 'var(--text-primary)',
              fontSize: '0.85rem',
              fontWeight: 700,
              width: 'auto'
            }}
          >
            <option value="">—</option>
            {Array.from({ length: MAX_QUESTIONS - 10 + 1 }, (_, i) => 10 + i).map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <label
          title="Highest variant letter the paper supports. Rows with a variant past this max get highlighted orange so you can fix them before exporting to EduPage. Required before approving."
          style={{
            fontSize: '0.85rem',
            fontWeight: 700,
            color: !maxVariant ? '#b45309' : '#1e40af',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            border: !maxVariant ? '2px solid #f59e0b' : '2px solid #3B82F6',
            background: !maxVariant ? 'rgba(245,158,11,0.10)' : 'rgba(59,130,246,0.08)',
            borderRadius: 6
          }}
        >
          Max var:
          <select
            value={maxVariant || ''}
            onChange={(e) => setMaxVariant(e.target.value || null)}
            style={{
              padding: '4px 4px',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: 4,
              background: 'var(--panel-bg, white)',
              color: 'var(--text-primary)',
              fontSize: '0.85rem',
              fontWeight: 700,
              width: 'auto'
            }}
          >
            <option value="">—</option>
            {['A','B','C','D','E','F','G','H','I','J'].map(letter => (
              <option key={letter} value={letter}>{letter}</option>
            ))}
          </select>
        </label>

        <button
          onClick={() => {
            setCalendarCollapsed(true);
            runOMR();
          }}
          disabled={!selectedFolderId || processing || mcqBusy}
          title={mcqBusy ? mcqBusyMsg : ''}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            background: !selectedFolderId || processing || mcqBusy
              ? '#9ca3af'
              : 'linear-gradient(135deg, #3B82F6, #2563EB)',
            color: 'white',
            cursor: !selectedFolderId || processing || mcqBusy ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 6px rgba(59,130,246,0.25)'
          }}
        >
          <FontAwesomeIcon icon={processing || mcqBusy ? faSpinner : faPlay} spin={processing || mcqBusy} />
          {processing
            ? 'Running…'
            : mcqBusy
              ? (aiStatus.mcq.source === 'auto' ? 'Auto-check running…' : 'Check busy…')
              : 'Start'}
        </button>

        {/* Recheck Selected — sits with Start Checking. Visible whenever
            there are rows, disabled until one or more checkboxes are ticked. */}
        {rows.length > 0 && (
          <button
            onClick={recheckSelected}
            disabled={processing || !!recheckSession || selectedRowIds.size === 0 || !model}
            title={
              selectedRowIds.size === 0
                ? 'Tick the checkbox on one or more rows to enable recheck.'
                : `Re-run OMR for ${selectedRowIds.size} selected row(s) — overwrites their current answers with a fresh AI read.`
            }
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              background: (processing || !!recheckSession || selectedRowIds.size === 0 || !model)
                ? '#9ca3af'
                : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
              color: 'white',
              cursor: (processing || !!recheckSession || selectedRowIds.size === 0 || !model) ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 6px rgba(139,92,246,0.25)'
            }}
          >
            <FontAwesomeIcon icon={recheckSession ? faSpinner : faRotate} spin={!!recheckSession} />
            {recheckSession ? 'Rechecking…' : `Recheck (${selectedRowIds.size})`}
          </button>
        )}

        {/* OMR — import bubble answers from omr_results.txt in this folder's
            LOCAL folder (external OMR-reader output) instead of running the AI. */}
        <button
          onClick={handleLoadOmr}
          disabled={!selectedFolderId || processing || omrLoading}
          title="Import MCQ answers from omr_results.txt in this folder's local folder (no AI run)"
          style={{
            padding: '8px 14px', border: 'none', borderRadius: '6px',
            background: (!selectedFolderId || processing || omrLoading) ? '#9ca3af' : 'linear-gradient(135deg, #CA8A04, #A16207)',
            color: 'white', cursor: (!selectedFolderId || processing || omrLoading) ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 6px rgba(202,138,4,0.3)'
          }}
        >
          <FontAwesomeIcon icon={omrLoading ? faSpinner : faFolderOpen} spin={omrLoading} />
          {omrLoading ? 'Loading…' : '1. OMR'}
        </button>

        {/* Mark Reviewed — placed right next to OMR (mustard pair) per request.
            Shown whenever there's a working table (after clicking the folder
            then OMR), regardless of whether a snapshot already exists or what
            its status is. Saves the current rows to the folder snapshot as
            "reviewed" in one step — no separate Save/Review needed first. */}
        {rows.length > 0 && !processing && (
          <button
            onClick={handleMarkReviewed}
            disabled={snapshotBusy}
            title={`Mark the saved table as REVIEWED. Updates the snapshot with your current working copy and flips status from "saved" → "reviewed".`}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              background: snapshotBusy
                ? '#9ca3af'
                : 'linear-gradient(135deg, #CA8A04, #A16207)',
              color: 'white',
              cursor: snapshotBusy ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 6px rgba(202,138,4,0.3)'
            }}
          >
            <FontAwesomeIcon icon={snapshotBusy ? faSpinner : faCheck} spin={snapshotBusy} />
            {snapshotBusy ? 'Marking…' : '2. Reviewed'}
          </button>
        )}

        {showClassTable && (
          <button
            onClick={handleCopyToEdupage}
            disabled={mcqCount == null || !maxVariant || selectedSnapStatus !== 'reviewed'}
            title={selectedSnapStatus !== 'reviewed'
              ? 'Mark this folder Reviewed first. That saves the checked table onto the folder before it can be copied to EduPage.'
              : (mcqCount == null || !maxVariant)
                ? 'Set MCQs and Max var before copying to EduPage.'
                : 'Copy this class’s variants and MCQ answers to EduPage'}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              background: (mcqCount == null || !maxVariant || selectedSnapStatus !== 'reviewed')
                ? '#9ca3af'
                : 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: 'white',
              cursor: (mcqCount == null || !maxVariant || selectedSnapStatus !== 'reviewed') ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 6px rgba(99,102,241,0.25)'
            }}
          >
            <FontAwesomeIcon icon={faCopy} />
            Copy to EduPage
          </button>
        )}

        {/* Review Saved Table (import) button removed — the new MCQ OMR flow is
            click folder (auto-loads its saved table) → OMR (extract from local
            omr_results.txt) → Reviewed (save to folder). The folder-select
            effect already pulls the saved snapshot into the working copy, so a
            separate manual import button is no longer needed. */}
        {false && (
          <button
            onClick={handleImportSnapshot}
            disabled={snapshotBusy}
            title={`Pull the saved table into your working copy so you can review it.`}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              background: snapshotBusy
                ? '#9ca3af'
                : 'linear-gradient(135deg, #9CA3AF, #6B7280)',
              color: 'white',
              cursor: snapshotBusy ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 6px rgba(107,114,128,0.3)'
            }}
          >
            <FontAwesomeIcon icon={snapshotBusy ? faSpinner : faFolderOpen} spin={snapshotBusy} />
            {snapshotBusy ? 'Loading…' : `Review`}
          </button>
        )}


        {processing && (
          <button
            onClick={stopOMR}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600
            }}
          >
            Stop
          </button>
        )}

        {/* Last Checked timestamp — pulled up from the (now-removed)
            results-table header. The sibling sheets-extracted pill that
            used to live here was moved to the extreme left of the action
            bar (see the very first child of this row). */}
        {rows.length > 0 && checkedAt && (
          <span
            title="When the OMR extraction last ran for this folder."
            style={{
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: 8,
              fontSize: '0.72rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fa-solid fa-clock" />
            {new Date(checkedAt).toLocaleString('en-PK', {
              timeZone: 'Asia/Karachi',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </span>
        )}
      </div>

      {/* Results table */}
      {rows.length > 0 && (
        <div style={{
          background: 'var(--card-bg, white)',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '12px',
          padding: '10px 12px 4px',
          overflow: calendarCollapsed ? 'hidden' : 'visible',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)',
          ...(calendarCollapsed ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : null)
        }}>
          {/* Results-table header removed per user request — the action
              bar above already carries the sheet count, folder name, and
              other state, so the table now starts directly. */}

          {/* One global stylesheet for the results table. Covers:
                - sticky header with elevation shadow
                - subtle row dividers + zebra striping
                - hover-row tint (live mouse position)
                - column hover tint (dynamic nth-child rule injected
                  alongside when hoveredColIdx is set)
                - smoothed transitions
              Inline cell colors (orange / blue / red status) win via
              cascade priority; col/row tints use background-image so
              they layer on top without obscuring the status colors. */}
          <style>{`
            .mcq-results-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              font-size: 0.78rem;
              table-layout: auto;
            }
            .mcq-results-table thead th {
              position: sticky;
              top: 0;
              z-index: 5;
              background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
              border-bottom: 2px solid #3B82F6;
              padding: 10px 8px;
              font-weight: 700;
              font-size: 0.72rem;
              letter-spacing: 0.02em;
              text-transform: uppercase;
              color: #334155;
              white-space: nowrap;
              user-select: none;
            }
            .mcq-results-table tbody td {
              padding: 6px;
              font-size: 0.78rem;
              color: var(--text-primary);
              white-space: nowrap;
              border-bottom: 1px solid rgba(148,163,184,0.18);
              /* Vertical divider between columns — easier to track which
                 answer belongs to which Q# when you have 30 narrow
                 columns side-by-side. last-child skips the trailing edge
                 so the table doesn't get a redundant right border. */
              border-right: 1px solid rgba(148,163,184,0.18);
              transition: background-color 80ms linear;
            }
            .mcq-results-table tbody td:last-child,
            .mcq-results-table thead th:last-child {
              border-right: none;
            }
            .mcq-results-table thead th {
              /* Stronger divider colour on the header so it visually
                 anchors each column from the very top. */
              border-right: 1px solid rgba(148,163,184,0.35);
            }
            /* Zebra striping — only applies when the row's inline style
               doesn't set its own background (i.e. not hovered, selected,
               or dup-roll). The row keeps inline transparent in those
               cases, so this rule shows through. */
            .mcq-results-table tbody tr:nth-child(even) > td {
              background-color: rgba(241,245,249,0.45);
            }
            .mcq-results-table tbody tr:hover > td {
              background-color: rgba(59,130,246,0.06);
            }
            ${hoveredColIdx != null ? `
              .mcq-results-table tr > *:nth-child(${hoveredColIdx + 1}) {
                background-image: linear-gradient(rgba(37,99,235,0.18), rgba(37,99,235,0.18)) !important;
              }
            ` : ''}
          `}</style>

          {(importedListsMeta.length > 1 || !showClassTable) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '2px 0 10px' }}>
              {importedListsMeta.length > 1 && (
                <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Student list:
                  <select
                    value={listFilter}
                    onChange={(e) => setListFilter(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.8rem' }}
                  >
                    <option value="all">All matching lists ({importedStudents.length})</option>
                    {importedListsMeta.map(l => {
                      const key = `${l.class}||${l.section || ''}||${l.subject}`;
                      return <option key={key} value={key}>{formatListLabel(l.class, l.section, l.subject)}</option>;
                    })}
                  </select>
                </label>
              )}
              {!showClassTable && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {processing
                    ? 'Checking — the class table opens when the run finishes and every roll matches.'
                    : importedStudentsLoading
                      ? 'Loading student list…'
                      : visibleStudents.length === 0
                        ? <span style={{ color: '#dc2626' }}>No matching student list for this folder's class + subject.</span>
                        : <>
                            <span>
                              Imported <strong>{visibleStudents.length}</strong>
                              {recon.unmatchedResults.length > 0 && <> · <strong>{recon.unmatchedResults.length}</strong> unmatched roll{recon.unmatchedResults.length === 1 ? '' : 's'}</>}
                              {recon.duplicateCount > 0 && <> · <strong>{recon.duplicateCount}</strong> duplicate roll{recon.duplicateCount === 1 ? '' : 's'}</>}
                              {recon.nameMismatchCount > 0 && <> · <strong>{recon.nameMismatchCount}</strong> name mismatch{recon.nameMismatchCount === 1 ? '' : 'es'}</>}
                              {recon.unmatchedResults.length > 0 || recon.duplicateCount > 0
                                ? ' — fix the rolls to continue.'
                                : recon.nameMismatchCount > 0
                                  ? ' — names do not have to match. Open the class table if these students are correct.'
                                  : ''}
                            </span>
                            {rollsClear && recon.nameMismatchCount > 0 && (
                              <button
                                type="button"
                                onClick={() => setConfirmedNameKey(nameMismatchKey)}
                                style={{
                                  padding: '4px 10px',
                                  border: 'none',
                                  borderRadius: 6,
                                  background: 'linear-gradient(135deg, #d97706, #b45309)',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Names are correct — open class table
                              </button>
                            )}
                          </>}
                </span>
              )}
            </div>
          )}

          {showClassTable ? (
            <McqClassTable
              fillHeight={calendarCollapsed}
              students={visibleStudents}
              match={sheetMatch}
              mcqCount={mcqCount}
              maxVariant={maxVariant}
              onUpdateField={updateHeaderField}
              onUpdateAnswer={updateAnswer}
              onUnmatch={unmatchSheet}
              openPreview={openPreview}
              selectedRowIds={selectedRowIds}
              onToggleRow={(id) => setSelectedRowIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })}
            />
          ) : (
            <McqReconciliation
              fillHeight={calendarCollapsed}
              students={visibleStudents}
              recon={recon}
              loading={importedStudentsLoading}
              onUpdateField={updateHeaderField}
              onDelete={dropRow}
              openPreview={openPreview}
            />
          )}
        </div>
      )}

      {rows.length === 0 && !processing && (
        <div style={{
          background: 'var(--card-bg, white)',
          border: '1px dashed var(--border-color, #e0e0e0)',
          borderRadius: '8px',
          padding: '14px',
          textAlign: 'center',
          color: 'var(--text-secondary)',
          fontSize: '0.82rem'
        }}>
          <FontAwesomeIcon icon={faRedo} style={{ fontSize: '1.1rem', marginRight: '6px', opacity: 0.5 }} />
          Pick a folder above and click <strong>Start</strong> to extract bubble-sheet answers.
        </div>
      )}

      <MessageBox
        message={message}
        onClose={() => setMessage({ show: false, text: '', type: 'info' })}
      />
      <ConfirmDialog
        confirm={confirm}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      {preview && (
        <MCQImagePreview
          items={preview.items}
          initialIndex={preview.index}
          onClose={() => setPreview(null)}
          width="15vw"
          dockRight
        />
      )}

    </div>
  );
}

// Most base styling for th / td (padding, color, font, borders, sticky
// header, zebra striping) lives in the .mcq-results-table CSS injected
// near the table. These constants only carry properties the CSS doesn't
// own — alignment defaults that vary per cell. Inline still wins over CSS,
// so per-cell overrides (status colors, padding:0 for editable cells,
// textAlign:'center' for short columns) continue to work via spreading.
const thStyle = {
  textAlign: 'left'
};

const tdStyle = {
  // Empty by design — see comment above. Spread is kept everywhere so
  // adding base properties later (e.g. min-height) is a one-line change.
};

const ellipsisCell = {
  display: 'inline-block',
  maxWidth: 140,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle'
};

// Eye-icon button used to open the scanned image preview.
const previewBtn = {
  marginLeft: 6,
  padding: '3px 9px',
  border: '1px solid #3B82F6',
  background: 'white',
  color: '#3B82F6',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.85rem',
  verticalAlign: 'middle'
};

// Trash-icon button next to the row index — drops the row from the
// OMR results entirely when the grader spots a wrong/duplicate scan.
const deleteBtn = {
  marginLeft: 4,
  padding: '3px 9px',
  border: '1px solid #dc2626',
  background: 'white',
  color: '#dc2626',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.85rem',
  verticalAlign: 'middle'
};

// Copy-row button — serialises the row to tab-separated values so the
// grader can paste it into Edupage / Excel directly.
const copyRowBtn = {
  marginLeft: 4,
  padding: '3px 9px',
  border: '1px solid #6366F1',
  background: 'white',
  color: '#6366F1',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.85rem',
  verticalAlign: 'middle'
};

// Clickable header variant — cursor:pointer + user-select disabled so a
// rapid double-click doesn't accidentally select the header text. Base
// styling is in the .mcq-results-table CSS.
const thSortable = {
  textAlign: 'left',
  cursor: 'pointer',
  userSelect: 'none'
};

// DOM-walks from the currently-editing input to the next editable cell in
// the given direction. Tagged spans/inputs carry `data-editable-cell="1"`
// so we can skip read-only cells (#, action buttons) by checking each
// candidate <td> in turn — the walk continues past any cell that has no
// editable element until it finds one or runs out of rows/columns.
//
// `dir` is 'left' | 'right' | 'up' | 'down'.
function findEditableInDirection(currentInput, dir) {
  const td = currentInput.closest('td');
  if (!td) return null;
  const tr = td.parentElement;
  if (!tr) return null;

  if (dir === 'left' || dir === 'right') {
    let candidate = dir === 'right' ? td.nextElementSibling : td.previousElementSibling;
    while (candidate) {
      const target = candidate.querySelector('[data-editable-cell="1"]');
      if (target) return target;
      candidate = dir === 'right' ? candidate.nextElementSibling : candidate.previousElementSibling;
    }
    return null;
  }

  // up/down — keep the same column index, hop rows.
  const tdIndex = Array.from(tr.children).indexOf(td);
  let targetTr = dir === 'down' ? tr.nextElementSibling : tr.previousElementSibling;
  while (targetTr) {
    const targetTd = targetTr.children[tdIndex];
    const target = targetTd && targetTd.querySelector('[data-editable-cell="1"]');
    if (target) return target;
    targetTr = dir === 'down' ? targetTr.nextElementSibling : targetTr.previousElementSibling;
  }
  return null;
}

// Inline-editable text cell. Renders as a span until clicked; swaps to an
// input that commits on blur or Enter and bails on Escape. Arrow keys also
// commit + navigate to the adjacent cell, spreadsheet-style:
//   • ArrowUp / ArrowDown        — always commit and move to the same column
//                                  in the previous / next row.
//   • ArrowLeft / ArrowRight     — only commit + move when the cursor is at
//                                  the very start / end of the input (so
//                                  arrow keys keep their native role of
//                                  moving the caret inside a multi-char
//                                  value). At the boundary they commit and
//                                  jump to the previous / next cell.
//
// compact=true → input sizes to its content (a few chars wide) instead of
// stretching to width:100%. Use for short-value columns (Variant, Class,
// Sec, MCQ answers) where width:100% blows the input up because
// `tableLayout: 'auto'` re-fits the column to the input's intrinsic size.
// Long-text columns (Name, file name) keep the default width:100%.
function EditableCell({ value, placeholder = '', onCommit, align = 'left', color, fontWeight = 400, compact = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  // Pick up external updates (e.g. re-run of OMR replaces the row) — but
  // only when we're not actively editing, otherwise the user's keystrokes
  // would get clobbered.
  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  if (!editing) {
    const display = value == null || value === '' ? placeholder : String(value);
    return (
      <span
        data-editable-cell="1"
        onClick={() => setEditing(true)}
        title="Click to edit · Arrow keys navigate between cells"
        style={{
          display: 'block',
          textAlign: align,
          color: color || (value ? '#111827' : '#9ca3af'),
          fontWeight,
          cursor: 'text',
          padding: '6px 4px',
          minHeight: 22,
          minWidth: 18
        }}
      >
        {display}
      </span>
    );
  }

  const commit = () => {
    setEditing(false);
    if ((draft || '') !== (value || '')) onCommit(draft);
  };
  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  // Commit current cell, then move focus to the editable element in the
  // target direction. We have to re-resolve the target inside a
  // requestAnimationFrame because commit() unmounts this input and React
  // hasn't re-rendered the new span yet — so by next frame the DOM is back
  // in span-mode and the navigation walk works.
  const commitAndNavigate = (input, dir) => {
    const target = findEditableInDirection(input, dir);
    commit();
    if (!target) return;
    requestAnimationFrame(() => {
      // The target might be either the previous-tick span (if still mounted)
      // or its replacement span after a re-render. Re-query by walking from
      // the same starting <td> just in case React re-keyed nodes.
      try { target.click(); } catch (_) {}
    });
  };

  return (
    <input
      autoFocus
      data-editable-cell="1"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        // Select all existing text on edit so typing replaces it immediately
        // (spreadsheet-style). Also covers arrow-key navigation into a cell.
        e.target.select();
      }}
      // Click-to-edit: the mouseup from the original click would otherwise
      // collapse the selection right after onFocus selects it.
      onMouseUp={(e) => e.preventDefault()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); return; }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); return; }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          commitAndNavigate(e.currentTarget, 'up');
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          commitAndNavigate(e.currentTarget, 'down');
          return;
        }
        if (e.key === 'ArrowLeft') {
          // Only commit + navigate when the caret is at the very start so
          // typed values stay scrubbable with the arrow keys.
          const input = e.currentTarget;
          if (input.selectionStart === 0 && input.selectionEnd === 0) {
            e.preventDefault();
            commitAndNavigate(input, 'left');
          }
          return;
        }
        if (e.key === 'ArrowRight') {
          const input = e.currentTarget;
          const atEnd = input.selectionStart === input.value.length &&
                        input.selectionEnd === input.value.length;
          if (atEnd) {
            e.preventDefault();
            commitAndNavigate(input, 'right');
          }
          return;
        }
      }}
      // For compact cells we use the `size` attribute (HTML-native width
      // hint, measured in chars) plus width:auto + maxWidth:100% so the
      // input stays narrow but never overflows its <td>. Long cells keep
      // width:100% as before.
      size={compact ? Math.max(2, (draft || '').length + 1) : undefined}
      style={{
        width: compact ? 'auto' : '100%',
        maxWidth: '100%',
        border: '2px solid #3B82F6',
        borderRadius: 3,
        padding: '4px 4px',
        fontSize: '0.78rem',
        fontWeight,
        color: color || '#111827',
        textAlign: align,
        background: 'white',
        outline: 'none',
        boxSizing: 'border-box'
      }}
    />
  );
}
